import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  GraduationCap, FileDown, Upload, ChevronLeft, Loader2, UserPlus,
  Pencil, Trash2, FileText, RefreshCcw, X, CheckCircle2, Eye, ShoppingCart,
  Activity, AlertTriangle, CalendarClock, Mail, History, Send, ScanBarcode,
  LogIn, CalendarDays, BookOpen, ClipboardList, ShieldCheck, Link2, QrCode,
  HardDrive, Search, ExternalLink, Save,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";
import { EntidadeFormadoraAviso } from "../components/EntidadeFormadora";
import { verifyEmail } from "@/shared/emailValidation";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return s; }
};

const fmtDateTime = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(s); }
};

// Calcula se o formando é menor à data de início do curso (< 18 anos).
const isMinor = (dataNascimento, dataInicioCurso) => {
  if (!dataNascimento || !dataInicioCurso) return false;
  const nasc = new Date(dataNascimento);
  const ini = new Date(dataInicioCurso);
  if (Number.isNaN(nasc.getTime()) || Number.isNaN(ini.getTime())) return false;
  let age = ini.getFullYear() - nasc.getFullYear();
  const m = ini.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && ini.getDate() < nasc.getDate())) age--;
  return age < 18;
};

const ALL_DOC_TIPOS = [
  {
    tipo: "ficha", label: "Ficha", forMinor: false,
    colPath: "FichaAssinadaPath",
    colEmailEm: "FichaConfirmadaEmailEm",
    colEmailPor: "FichaConfirmadaEmailPor",
  },
  {
    tipo: "contrato", label: "Contrato", forMinor: false,
    colPath: "ContratoAssinadoPath",
    colEmailEm: "ContratoConfirmadoEmailEm",
    colEmailPor: "ContratoConfirmadoEmailPor",
  },
  {
    tipo: "menor", label: "Menor", forMinor: true,
    colPath: "MenorAssinadoPath",
    colEmailEm: "MenorConfirmadoEmailEm",
    colEmailPor: "MenorConfirmadoEmailPor",
  },
];

const signedHref = (cursoId, fichaId, tipo) =>
  ("" || "") +
  `/api/admin/cursos/${cursoId}/inscricoes/${fichaId}/assinado/${tipo}/download`;

// ── Chip "Comprou produto" (mostrado em cada linha da tabela de inscrições)
// ── Link Progress Chip ──────────────────────────────────────────────
// Mostra o progresso de preenchimento do link de inscrição:
// "N/M preenchidas — K por tratar". Cor verde quando 100% preenchido,
// amarela enquanto faltam vagas. Dois ou mais formandos com o mesmo
// `ID_LinkInscricao` partilham o mesmo chip (mesma compra → mesmo link).
function LinkProgressChip({ lp }) {
  if (!lp || !lp.quantidade) return null;
  const { quantidade, preenchidas, por_tratar, comprador_nome } = lp;
  const ok = por_tratar === 0 && preenchidas === quantidade;
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-amber-50 text-amber-800 border-amber-200";
  const title = (
    `Comprador: ${comprador_nome || "—"}\n` +
    `Total comprado: ${quantidade} vaga${quantidade === 1 ? "" : "s"}\n` +
    `Preenchidas: ${preenchidas}\n` +
    `Por tratar: ${por_tratar}`
  );
  return (
    <span
      data-testid="link-progress-chip"
      className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${cls}`}
      title={title}
    >
      <ClipboardList size={9} />
      <span>{preenchidas}/{quantidade}</span>
      {por_tratar > 0 && <span className="opacity-80">· {por_tratar} por tratar</span>}
    </span>
  );
}

function CompraChip({ compra }) {
  if (!compra) {
    return (
      <span
        data-testid="compra-chip-none"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
        title="Não existe nenhuma encomenda do produto deste curso para este cliente"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Sem compra
      </span>
    );
  }
  const ec = compra.ID_EstadoEncomenda;
  // Estados completos ficam verdes; pendentes/em fluxo (FE, PA, EV) ficam amarelados.
  const isPaid = ec === "EC" || ec === "PR";
  const cls = isPaid
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-blue-50 text-blue-700 border-blue-200";
  return (
    <span
      data-testid={`compra-chip-${compra.ID_Encomenda}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${cls}`}
      title={`Estado: ${compra.EstadoEncomenda || ec}`}
    >
      <ShoppingCart size={9} />
      <span className="font-mono">#{compra.ID_Encomenda}</span>
      <span className="opacity-70">{ec}</span>
    </span>
  );
}

// ── Audit Timeline Modal ───────────────────────────────────────────
// Mostra a sequência de eventos do formando: abertura do link, criação/edição
// da ficha, downloads, uploads. Aberto via botão "histórico" por linha.
const TIMELINE_ICON_MAP = {
  link_opened: { icon: "🔗", tone: "neutral" },
  form_view: { icon: "👁", tone: "neutral" },
  form_create: { icon: "📝", tone: "ok" },
  form_update: { icon: "✏️", tone: "neutral" },
  form_confirm: { icon: "✅", tone: "ok" },
  download_ficha: { icon: "⬇️", tone: "info" },
  download_contrato: { icon: "⬇️", tone: "info" },
  download_menor: { icon: "⬇️", tone: "info" },
  upload_ficha: { icon: "📤", tone: "ok" },
  upload_contrato: { icon: "📤", tone: "ok" },
  upload_menor: { icon: "📤", tone: "ok" },
};

function AuditTimelineModal({ open, onClose, cursoId, ficha }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !ficha?.ID_FichaInscricao) return;
    setLoading(true); setError(null);
    adminApi.cursoInscricaoAudit(cursoId, ficha.ID_FichaInscricao)
      .then((r) => setEvents(r.events || []))
      .catch((e) => setError(e.message || "Erro a carregar histórico"))
      .finally(() => setLoading(false));
  }, [open, cursoId, ficha?.ID_FichaInscricao]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 sm:p-8 overflow-auto" data-testid="audit-modal">
      <div className="bg-white border border-[#E6E1D6] rounded-md w-full max-w-2xl shadow-xl">
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#E6E1D6]">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Histórico de actividade</h2>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              {ficha?.NomeCompleto || ""} · ficha #{ficha?.ID_FichaInscricao}
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="audit-modal-close"
            className="p-1 hover:bg-[#FAF8F2] rounded">
            <X size={16} />
          </button>
        </header>
        <div className="p-4">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
              <Loader2 size={12} className="animate-spin" /> A carregar…
            </div>
          )}
          {error && <p className="text-xs text-red-700">{error}</p>}
          {!loading && !error && events.length === 0 && (
            <p className="text-xs text-[#6B7280] italic">
              Sem actividade registada ainda. O formando provavelmente não abriu o link.
            </p>
          )}
          {!loading && events.length > 0 && (
            <ol className="space-y-2" data-testid="audit-timeline-list">
              {events.map((ev) => {
                const cfg = TIMELINE_ICON_MAP[ev.event] || { icon: "•", tone: "neutral" };
                const tone =
                  cfg.tone === "ok" ? "border-emerald-200 bg-emerald-50"
                  : cfg.tone === "info" ? "border-sky-200 bg-sky-50"
                  : "border-[#E6E1D6] bg-[#FAF8F2]";
                const dt = ev.at ? new Date(ev.at) : null;
                const dtStr = dt ? dt.toLocaleString("pt-PT", {
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                }) : "—";
                return (
                  <li key={ev._id} className={`px-3 py-2 border rounded ${tone}`} data-testid={`audit-event-${ev.event}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-base leading-none">{cfg.icon}</span>
                      <span className="font-medium text-[#0F1829] flex-1">{ev.label}</span>
                      <span className="text-[10px] text-[#6B7280]">{dtStr}</span>
                    </div>
                    {(ev.ip || ev.user_agent || (ev.meta && Object.keys(ev.meta).length > 0)) && (
                      <div className="text-[10px] text-[#9B9079] mt-1 pl-7 truncate" title={`IP: ${ev.ip || "?"} · UA: ${ev.user_agent || "?"}`}>
                        {ev.ip && <span>IP {ev.ip}</span>}
                        {ev.meta?.format && <span> · {ev.meta.format.toUpperCase()}</span>}
                        {ev.meta?.size && <span> · {Math.round(ev.meta.size / 1024)}KB</span>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Email Link pill ────────────────────────────────────────────────
function EmailLinkPill({ ficha, cursoId, onResent }) {
  const [busy, setBusy] = useState(false);

  const resend = async () => {
    const label = ficha.EmailEnviado === "S" ? "Reenviar" : "Enviar agora";
    if (!window.confirm(
      `${label} o email com o link de inscrição para:\n\n` +
      `${ficha.NomeCompleto}\n${ficha.Email || "(sem email)"}\n\n` +
      `Confirma?`
    )) return;
    setBusy(true);
    try {
      const r = await adminApi.cursoResendEmail(cursoId, ficha.ID_FichaInscricao);
      toast.success(`Email enviado para ${r.email}.`);
      if (onResent) onResent();
    } catch (e) {
      toast.error(e.message || "Erro a reenviar email");
    } finally { setBusy(false); }
  };

  if (!ficha.ID_LinkInscricao) {
    return (
      <button
        type="button"
        onClick={resend}
        disabled={busy}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#F3F0E7] text-[#978D78] border border-[#E6E1D6] hover:bg-[#E8E1CC] disabled:opacity-50 transition"
        title="Sem link gerado — clique para gerar e enviar agora."
        data-testid={`email-link-none-${ficha.ID_FichaInscricao}`}
      >
        {busy ? <Loader2 size={9} className="animate-spin" /> : <Mail size={9} />}
        Sem link
      </button>
    );
  }
  const enviado = ficha.EmailEnviado === "S";
  if (!enviado) {
    return (
      <button
        type="button"
        onClick={resend}
        disabled={busy}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 border border-amber-200 font-medium hover:bg-amber-100 disabled:opacity-50 transition"
        title="Link gerado mas email ainda não enviado — clique para enviar agora."
        data-testid={`email-link-pending-${ficha.ID_FichaInscricao}`}
      >
        {busy ? <Loader2 size={9} className="animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
        Pendente
      </button>
    );
  }
  const d = ficha.DataEmailEnviado;
  let label = "Enviado";
  if (d) {
    try {
      label = `Enviado ${new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" })}`;
    } catch { /* keep default */ }
  }
  return (
    <button
      type="button"
      onClick={resend}
      disabled={busy}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium hover:bg-emerald-100 disabled:opacity-50 transition"
      title={d
        ? `Enviado em ${new Date(d).toLocaleString("pt-PT")}.\nClique para reenviar.`
        : "Email enviado. Clique para reenviar."}
      data-testid={`email-link-sent-${ficha.ID_FichaInscricao}`}
    >
      {busy ? <Loader2 size={9} className="animate-spin" /> : <CheckCircle2 size={9} />}
      {label}
    </button>
  );
}

// ── Pills com check-ins (presenças) do formando ──────────────────────
// Mostra um pill por cada entrada registada (sessão N, data e hora).
// Vazio quando o formando ainda não fez check-in em nenhuma sessão.
function CheckinPills({ checkins }) {
  if (!checkins || checkins.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 text-[10px] mt-1" data-testid="checkin-pills">
      {checkins.map((c) => (
        <span
          key={c.ID_CheckInSessao || `${c.Ordem}-${c.DataCheckIn}`}
          data-testid={`checkin-pill-${c.Ordem}`}
          title={`Sessão ${c.Ordem} — entrada registada em ${fmtDateTime(c.DataCheckIn)}${c.CheckInPor ? ` por ${c.CheckInPor}` : ""}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
        >
          <LogIn size={9} />
          Sessão {c.Ordem} · {fmtDateTime(c.DataCheckIn)}
        </span>
      ))}
    </div>
  );
}

// ── Status pill com resumo do estado dos 3 docs ────────────────────
//
// 3 estados (Sprint 1G):
//   🟢 ENTREGUE (Upload)  — Cliente fez upload do PDF → pill VERDE com check.
//   🔵 ENTREGUE (Email)   — Admin confirmou recepção por email → pill AZUL com envelope.
//   ⚠️ PENDENTE           — Nada recebido → pill AMARELA com ponto.
function DocStatusPills({ ficha, minor }) {
  // A repetir o curso: não preenche docs, não vai para fichas de presença.
  // Em vez dos pills habituais, mostra apenas um badge informativo.
  if (ficha.repetente) {
    return (
      <div className="flex flex-wrap gap-1 text-[10px]">
        <span
          data-testid={`status-repetente-${ficha.ID_FichaInscricao}`}
          title="Formando(a) está a repetir o curso — não precisa preencher documentos nem aparece nas fichas de presença"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200"
        >
          <RefreshCcw size={9} /> A repetir
        </span>
      </div>
    );
  }
  const tipos = minor ? ALL_DOC_TIPOS : ALL_DOC_TIPOS.filter((t) => !t.forMinor);
  return (
    <div className="flex flex-wrap gap-1 text-[10px]">
      {tipos.map(({ tipo, label, colPath, colEmailEm, colEmailPor }) => {
        const uploaded = Boolean(ficha[colPath]);
        const emailConfirmed = Boolean(ficha[colEmailEm]);
        const emailEm = ficha[colEmailEm];
        const emailPor = ficha[colEmailPor];
        let cls, icon, tooltip;
        if (uploaded) {
          cls = "bg-emerald-50 text-emerald-700 border border-emerald-200";
          icon = <CheckCircle2 size={9} />;
          tooltip = `${label}: Upload recebido`;
        } else if (emailConfirmed) {
          cls = "bg-sky-50 text-sky-700 border border-sky-200";
          icon = <Mail size={9} />;
          const dt = emailEm ? new Date(emailEm).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
          tooltip = `${label}: Recebido por email${emailPor ? ` por ${emailPor}` : ""}${dt ? ` em ${dt}` : ""}`;
        } else {
          cls = "bg-amber-50 text-amber-700 border border-amber-200";
          icon = <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />;
          tooltip = `${label}: pendente — nem upload nem confirmação por email`;
        }
        return (
          <span key={tipo}
            data-testid={`status-${tipo}-${ficha.ID_FichaInscricao}`}
            data-state={uploaded ? "upload" : emailConfirmed ? "email" : "pendente"}
            title={tooltip}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${cls}`}>
            {icon}
            {label}
          </span>
        );
      })}
      {minor && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-semibold"
          data-testid={`minor-flag-${ficha.ID_FichaInscricao}`}>
          Menor
        </span>
      )}
      {ficha.ficha_incompleta && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 font-semibold"
          title="Ficha criada com defaults sentinel (Nome+Email apenas) — falta NIF, Data Nascimento e/ou Nº Documento. Edite para completar."
          data-testid={`ficha-incompleta-${ficha.ID_FichaInscricao}`}
        >
          ⚠ Incompleta
        </span>
      )}
    </div>
  );
}

// ── Per-document actions cell (gerar + upload assinado + ver/remover + confirmar email) ──
//
// Sprint 1G — Cada documento (Ficha / Contrato / Menor) tem 3 estados claros:
//
//   🟢 ENTREGUE (UPLOAD)  — Cliente fez upload do PDF assinado.
//      Ícone: olho (Eye) verde → abre o ficheiro. X → remover upload.
//
//   🔵 ENTREGUE (EMAIL)   — Admin marcou que recebeu por email.
//      Ícone: envelope (Mail) azul + data + email do admin no tooltip.
//      X → desmarcar.
//
//   ⚪ PENDENTE          — Nem upload nem confirmação de email.
//      Ícone: upload (Upload) cinza → abre selector de ficheiro.
//      Ícone: envelope (Mail) cinza → "Marcar como recebido por email".
//
// Os botões "Gerar" (FileDown) continuam sempre disponíveis para download
// do template preenchido a partir dos dados da ficha.
function DocActionsCell({ cursoId, ficha, minor, onChanged }) {
  const [uploading, setUploading] = useState("");
  const [confirming, setConfirming] = useState("");

  const uploadFileDirect = async (tipo, file) => {
    setUploading(tipo);
    try {
      await adminApi.cursoAssinadoUpload(cursoId, ficha.ID_FichaInscricao, tipo, file);
      toast.success(`${tipo} digitalizado carregado`);
      onChanged();
    } catch (err) {
      toast.error(err.message || "Erro upload");
    } finally { setUploading(""); }
  };

  const onUpload = async (tipo, e) => {
    const all = Array.from(e.target.files || []);
    if (!all.length) return;
    if (all.length > 2) {
      toast.error("Máximo 2 ficheiros");
      e.target.value = "";
      return;
    }
    // Quando 2 ficheiros, DOCX não é permitido (só PDF/imagem para o backend
    // fazer merge num único PDF). Single-file aceita também DOCX.
    if (all.length === 2 && all.some((f) => (f.name || "").toLowerCase().endsWith(".docx"))) {
      toast.error("Quando envia 2 ficheiros, só pode usar PDF e imagens (DOCX só funciona em ficheiro único)");
      e.target.value = "";
      return;
    }
    setUploading(tipo);
    try {
      await adminApi.cursoAssinadoUpload(cursoId, ficha.ID_FichaInscricao, tipo, all.length === 1 ? all[0] : all);
      toast.success(all.length === 2 ? `${tipo} (2 páginas combinadas) carregado` : `${tipo} assinado carregado`);
      onChanged();
    } catch (err) {
      toast.error(err.message || "Erro upload");
    } finally { setUploading(""); e.target.value = ""; }
  };

  const onRemoveUpload = async (tipo) => {
    if (!window.confirm(`Remover upload de ${tipo}?`)) return;
    try {
      await adminApi.cursoAssinadoDelete(cursoId, ficha.ID_FichaInscricao, tipo);
      toast.success("Upload removido");
      onChanged();
    } catch (err) { toast.error(err.message || "Erro"); }
  };

  const onConfirmEmail = async (tipo, confirmar) => {
    if (confirmar && !window.confirm(
      `Marcar ${tipo} como recebida por email?\n\n` +
      `Use esta opção quando o formando enviou o documento assinado por email ` +
      `em vez de fazer upload no portal. Fica registado quem confirmou e quando.`
    )) return;
    if (!confirmar && !window.confirm(`Remover confirmação de recepção por email de ${tipo}?`)) return;
    setConfirming(tipo);
    try {
      await adminApi.cursoConfirmarEmail(cursoId, ficha.ID_FichaInscricao, tipo, confirmar);
      toast.success(confirmar ? "Marcado como recebido por email" : "Marca de email removida");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Erro");
    } finally { setConfirming(""); }
  };

  const tipos = minor ? ALL_DOC_TIPOS : ALL_DOC_TIPOS.filter((t) => !t.forMinor);

  // Repetentes não recebem documentos — UI desactivada (backend devolve 409
  // mesmo que o admin tente forçar via URL directa).
  if (ficha.repetente) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider border border-indigo-200 bg-indigo-50 text-indigo-700"
        title="Formando 'A repetir o curso' — não recebe documentos, diploma nem avaliação"
        data-testid={`doc-cell-repetente-${ficha.ID_FichaInscricao}`}
      >
        <RefreshCcw size={11} /> Repetente · sem documentos
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {tipos.map(({ tipo, label, colPath, colEmailEm, colEmailPor }) => {
        const uploaded = Boolean(ficha[colPath]);
        const emailConfirmed = Boolean(ficha[colEmailEm]);
        const emailEm = ficha[colEmailEm];
        const emailPor = ficha[colEmailPor];
        const isBusy = uploading === tipo || confirming === tipo;
        return (
          <div
            key={tipo}
            className={`inline-flex items-stretch border rounded overflow-hidden ${
              uploaded ? "border-emerald-300 bg-emerald-50/30"
                : emailConfirmed ? "border-sky-300 bg-sky-50/30"
                : "border-[#E6E1D6]"
            }`}
            data-testid={`doc-cell-${tipo}-${ficha.ID_FichaInscricao}`}
            data-state={uploaded ? "upload" : emailConfirmed ? "email" : "pendente"}
          >
            {/* Botão Gerar PDF (Reportlab) — sempre disponível */}
            <a href={adminApi.cursoDocumentoPdfUrl(cursoId, ficha.ID_FichaInscricao, tipo)}
              target="_blank" rel="noopener noreferrer"
              className="px-2 py-1 text-[11px] hover:bg-[#FAF8F2] inline-flex items-center gap-1 border-r border-inherit"
              title={`Gerar ${label} em PDF (Reportlab)`}
              data-testid={`gen-${tipo}-${ficha.ID_FichaInscricao}`}>
              <FileDown size={10} /> {label}
            </a>

            {/* Estado UPLOAD: olho + X remover */}
            {uploaded && (
              <>
                <a href={signedHref(cursoId, ficha.ID_FichaInscricao, tipo)}
                  target="_blank" rel="noopener noreferrer"
                  className="px-1.5 grid place-items-center hover:bg-emerald-100 text-emerald-700"
                  title={`📤 Upload recebido — ver ${label} (abre nova aba)`}
                  data-testid={`signed-view-${tipo}-${ficha.ID_FichaInscricao}`}>
                  <Eye size={11} />
                </a>
                <button onClick={() => onRemoveUpload(tipo)}
                  className="px-1 grid place-items-center hover:bg-red-50 text-red-600"
                  title={`Remover upload de ${label}`}
                  data-testid={`signed-rm-${tipo}-${ficha.ID_FichaInscricao}`}>
                  <X size={11} />
                </button>
              </>
            )}

            {/* Estado EMAIL: envelope azul + X desmarcar */}
            {!uploaded && emailConfirmed && (
              <>
                <span
                  className="px-1.5 grid place-items-center text-sky-700"
                  title={`✉️ Recebido por email${emailPor ? ` por ${emailPor}` : ""}${emailEm ? ` em ${new Date(emailEm).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}`}
                  data-testid={`email-mark-${tipo}-${ficha.ID_FichaInscricao}`}
                >
                  <Mail size={11} />
                </span>
                <button onClick={() => onConfirmEmail(tipo, false)}
                  disabled={isBusy}
                  className="px-1 grid place-items-center hover:bg-red-50 text-red-600 disabled:opacity-50"
                  title={`Desmarcar recepção por email de ${label}`}
                  data-testid={`email-rm-${tipo}-${ficha.ID_FichaInscricao}`}>
                  {isBusy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
                </button>
              </>
            )}

            {/* Estado PENDENTE: upload + digitalizar + envelope para marcar email */}
            {!uploaded && !emailConfirmed && (
              <>
                <label
                  className="px-1.5 grid place-items-center hover:bg-[#FAF8F2] cursor-pointer"
                  title={tipo === "contrato"
                    ? "Carregar contrato assinado (PDF/imagem/Word) — pode escolher 2 PDFs/imagens para juntar"
                    : `Carregar ${label} assinado (PDF/imagem/Word)`}
                  data-testid={`signed-up-${tipo}-${ficha.ID_FichaInscricao}`}
                >
                  {uploading === tipo ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                  <input type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" className="hidden"
                    multiple={tipo === "contrato"}
                    onChange={(e) => onUpload(tipo, e)} disabled={isBusy} />
                </label>
                <button
                  onClick={() => onConfirmEmail(tipo, true)}
                  disabled={isBusy}
                  className="px-1.5 grid place-items-center hover:bg-sky-50 text-sky-700 disabled:opacity-50 border-l border-inherit"
                  title={`Marcar ${label} como recebida por email (sem upload)`}
                  data-testid={`email-confirm-${tipo}-${ficha.ID_FichaInscricao}`}
                >
                  {confirming === tipo ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Check-in KPI Strip (auto-refresh) ─────────────────────────────
function CheckinKpiStrip({ cursoId, expectedTotal }) {
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await adminApi.cursoCheckinSummary(cursoId);
        if (alive) { setData(r); setUpdatedAt(new Date()); }
      } catch { /* silent — pode acontecer durante login expiry */ }
    };
    load();
    const id = setInterval(load, 30_000); // 30s
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [cursoId]);

  if (!data) return null;
  const hasSessions = (data.por_sessao || []).length > 0;
  const fichasInscritas = data.total_inscritos || 0;
  // Denominador unificado: privilegia `expectedTotal` (compradores +
  // externos manuais), mas garante >= fichas já criadas (caso o widget seja
  // renderizado antes do parent calcular).
  const totalEsperado = Math.max(Number(expectedTotal) || 0, fichasInscritas);
  const totalValidados = data.total_validados || 0;
  const valPct = totalEsperado ? Math.round((totalValidados / totalEsperado) * 100) : 0;

  return (
    <section className="p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid="checkin-kpi-strip">
      <div className="flex items-center gap-2 mb-3">
        <ScanBarcode size={14} className="text-[#0F1829]" />
        <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Presenças em tempo real</h2>
        {updatedAt && (
          <span className="ml-auto text-[10px] text-[#978D78]" data-testid="checkin-kpi-updated">
            actualizado às {updatedAt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <KpiCard
          label="Inscritos"
          value={totalEsperado}
          sub={totalEsperado !== fichasInscritas ? `${fichasInscritas} ficha${fichasInscritas === 1 ? "" : "s"} criada${fichasInscritas === 1 ? "" : "s"}` : null}
          testid="checkin-kpi-inscritos"
        />
        <KpiCard label="Validados" value={`${totalValidados}/${totalEsperado}`} sub={`${valPct}%`} testid="checkin-kpi-validados" />
        <KpiCard label="Sessões" value={(data.por_sessao || []).length} testid="checkin-kpi-sessoes" />
        <KpiCard label="Total presenças" value={(data.por_sessao || []).reduce((s, p) => s + (p.presentes || 0), 0)} testid="checkin-kpi-total-presencas" />
      </div>

      {hasSessions && (
        <div className="border-t border-[#F3F0E7] pt-2.5">
          <p className="text-[10px] uppercase tracking-wider text-[#978D78] mb-2">Por sessão</p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
            {data.por_sessao.map((s) => {
              const pct = totalEsperado ? Math.round((s.presentes / totalEsperado) * 100) : 0;
              return (
                <li key={s.ordem} data-testid={`checkin-kpi-sess-${s.ordem}`}
                  className="px-2.5 py-1.5 bg-[#FAF8F2] border border-[#F3F0E7] rounded flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#978D78]">Sessão {s.ordem}</span>
                  <span className="ml-auto font-bold text-sm text-[#0F1829]">
                    {s.presentes}<span className="text-[10px] text-[#978D78]">/{totalEsperado}</span>
                  </span>
                  <span className="text-[10px] text-[#6B7280]">{pct}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function KpiCard({ label, value, sub, testid }) {
  return (
    <div className="p-2.5 bg-[#FAF8F2] border border-[#F3F0E7] rounded" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#978D78]">{label}</p>
      <p className="text-xl font-bold text-[#0F1829] leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-[#6B7280]">{sub}</p>}
    </div>
  );
}


// ── Operadores Check-in modal (Sprint 1F+) ───────────────────────
function CheckinOperadoresButton({ cursoId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="checkin-ops-open"
        title="Gerir operadores autorizados a fazer check-in neste curso"
        className="text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1.5">
        <ScanBarcode size={12} /> Operadores
      </button>
      {open && <CheckinOperadoresModal cursoId={cursoId} onClose={() => setOpen(false)} />}
    </>
  );
}

function CheckinOperadoresModal({ cursoId, onClose }) {
  const [ops, setOps] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [acting, setActing] = useState(0);

  const load = async () => {
    try {
      const r = await adminApi.cursoCheckinOpsList(cursoId);
      setOps(r.data || []);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load();   }, [cursoId]);

  useEffect(() => {
    if (q.trim().length < 3) { setResults([]); return; }
    const id = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await adminApi.cursoCheckinClientesSearch(q.trim());
        setResults(r.data || []);
      } catch (e) { toast.error(e.message); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const add = async (cli) => {
    setActing(cli.ID_Cliente);
    try {
      await adminApi.cursoCheckinOpsAdd(cursoId, cli.ID_Cliente);
      toast.success(`${cli.NomeCompleto} adicionado como operador`);
      setQ(""); setResults([]);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setActing(0); }
  };

  const remove = async (cli) => {
    if (!window.confirm(`Remover ${cli.NomeCompleto} dos operadores deste curso?`)) return;
    setActing(cli.ID_Cliente);
    try {
      await adminApi.cursoCheckinOpsRemove(cursoId, cli.ID_Cliente);
      toast.success("Operador removido");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setActing(0); }
  };

  const opIds = new Set(ops.map((o) => o.ID_Cliente));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" data-testid="checkin-ops-modal">
      <div className="bg-white shadow-xl w-full sm:max-w-xl sm:rounded-md rounded-t-2xl flex flex-col"
        style={{ maxHeight: "92vh" }}>
        {/* Handle visual de mobile sheet */}
        <div className="sm:hidden flex justify-center pt-2">
          <div className="w-10 h-1.5 bg-[#E6E1D6] rounded-full" />
        </div>

        <div className="flex items-center gap-2 px-4 py-3 sm:px-5 sm:py-4 border-b border-[#E6E1D6]">
          <ScanBarcode size={18} className="text-[#0F1829]" />
          <h3 className="text-sm font-bold uppercase tracking-[0.12em] flex-1">Operadores de Check-in</h3>
          <button onClick={onClose}
            className="p-2 -mr-2 text-[#6B7280] hover:text-[#0F1829] active:bg-[#FAF8F2] rounded"
            data-testid="checkin-ops-close" aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 space-y-5 text-sm">
          <p className="text-xs text-[#6B7280] leading-relaxed">
            Estes utilizadores podem fazer login em <code className="text-[11px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 rounded">/admin/cursos/checkin/login</code> com o email/password do registo IHTP e registar entradas <strong>só neste curso</strong>.
          </p>

          {/* Lista atual */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#978D78] mb-2">
              Operadores activos · {ops.length}
            </p>
            {ops.length === 0 ? (
              <p className="text-xs text-[#6B7280] italic">Nenhum operador atribuído ainda.</p>
            ) : (
              <ul className="space-y-2">
                {ops.map((o) => (
                  <li key={o.ID_Cliente} className="flex items-center gap-3 p-3 bg-[#FAF8F2] border border-[#F3F0E7] rounded-lg"
                    data-testid={`checkin-op-${o.ID_Cliente}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0F1829] truncate text-sm">{o.NomeCompleto}</p>
                      <p className="text-[11px] text-[#6B7280] truncate">{o.Email}</p>
                      {o.Nif && <p className="text-[10px] text-[#978D78]">NIF {o.Nif}</p>}
                    </div>
                    <button onClick={() => remove(o)} disabled={acting === o.ID_Cliente}
                      data-testid={`checkin-op-remove-${o.ID_Cliente}`}
                      aria-label={`Remover ${o.NomeCompleto}`}
                      className="p-2.5 text-red-600 border border-red-200 rounded-lg active:bg-red-50 disabled:opacity-50 touch-manipulation">
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Adicionar */}
          <div className="pt-4 border-t border-[#E6E1D6]">
            <p className="text-[11px] uppercase tracking-wider text-[#978D78] mb-2">Adicionar operador</p>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Procurar nome, email ou NIF..."
              data-testid="checkin-ops-search"
              autoComplete="off"
              className="w-full px-4 py-3 text-base border border-[#E6E1D6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F1829]/20" />
            {searching && <p className="text-[11px] text-[#978D78] mt-2">A pesquisar...</p>}
            {!searching && q.trim().length >= 3 && results.length === 0 && (
              <p className="text-[11px] text-[#978D78] mt-2">Sem resultados.</p>
            )}
            {q.trim().length > 0 && q.trim().length < 3 && (
              <p className="text-[11px] text-[#978D78] mt-2">Escreve pelo menos 3 caracteres...</p>
            )}
            {results.length > 0 && (
              <ul className="mt-3 space-y-1.5 max-h-72 overflow-y-auto rounded-lg border border-[#F3F0E7]">
                {results.map((c) => {
                  const alreadyOp = opIds.has(c.ID_Cliente);
                  return (
                    <li key={c.ID_Cliente} className="flex items-center gap-3 p-3 border-b border-[#F3F0E7] last:border-b-0"
                      data-testid={`checkin-search-${c.ID_Cliente}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{c.NomeCompleto}</p>
                        <p className="text-[11px] text-[#6B7280] truncate">{c.Email}</p>
                        {c.Nif && <p className="text-[10px] text-[#978D78]">NIF {c.Nif}</p>}
                      </div>
                      <button onClick={() => add(c)} disabled={alreadyOp || acting === c.ID_Cliente}
                        data-testid={`checkin-search-add-${c.ID_Cliente}`}
                        className="text-xs px-3 py-2 bg-[#0F1829] text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed active:bg-[#1A2540] touch-manipulation whitespace-nowrap">
                        {alreadyOp ? "Já é operador" : (acting === c.ID_Cliente ? "..." : "+ Adicionar")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="px-4 py-3 sm:px-5 sm:py-3 border-t border-[#E6E1D6] flex justify-end" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} data-testid="checkin-ops-done"
            className="text-sm px-5 py-2.5 bg-[#0F1829] text-white rounded-lg hover:bg-[#1A2540] active:bg-[#1A2540] touch-manipulation">Fechar</button>
        </div>
      </div>
    </div>
  );
}


// ── Templates Card ─────────────────────────────────────────────────
function ImportExcelButton({ cursoId, onImported }) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null); // { total, created, skipped, errors, warnings }

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await adminApi.cursoBulkFromExcel(cursoId, file);
      setResult(res);
      const created = (res.created || []).length;
      const skipped = (res.skipped || []).length;
      const errors = (res.errors || []).length;
      if (created) toast.success(`Importação Excel: ${created} criadas, ${skipped} ignoradas, ${errors} erros.`);
      else toast.warning(`Importação Excel terminou sem novas inscrições. ${skipped} já existiam, ${errors} erros.`);
      onImported?.();
    } catch (err) {
      toast.error(err.message || "Erro a importar Excel");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <label className="text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#FAF8F2] cursor-pointer inline-flex items-center gap-1.5"
        data-testid="insc-import-excel"
        title="Importar formandos a partir de ficheiro Excel">
        {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {importing ? "A importar..." : "Importar Excel"}
        <input type="file" accept=".xlsx" className="hidden" onChange={onPick} disabled={importing} />
      </label>
      <a
        href={adminApi.cursoExcelTemplateUrl()}
        data-testid="insc-import-excel-template"
        title="Descarregar template Excel"
        className="text-[11px] text-[#6B7280] hover:text-[#0F1829] underline">
        template
      </a>
      <a
        href={adminApi.cursoExportSigoUrl(cursoId)}
        data-testid="insc-export-sigo"
        title="Exportar fichas confirmadas no layout do template SIGO (CFORM)"
        className="text-xs px-3 py-1.5 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1.5">
        <FileDown size={12} /> Exportar p/ SIGO
      </a>
      {result && (
        <ImportExcelResultModal result={result} onClose={() => setResult(null)} />
      )}
    </>
  );
}

function ImportExcelResultModal({ result, onClose }) {
  const created = result.created || [];
  const skipped = result.skipped || [];
  const errors = result.errors || [];
  const warnings = result.warnings || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="import-excel-result-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center gap-2 p-4 border-b border-[#E6E1D6]">
          <CheckCircle2 size={16} className="text-[#0F1829]" />
          <h3 className="text-sm font-bold uppercase tracking-[0.12em]">Resultado da Importação</h3>
          <button onClick={onClose} className="ml-auto text-[#6B7280] hover:text-[#0F1829]" data-testid="import-excel-result-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-4 text-xs">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-[#E6F4EA] rounded border border-[#A6DFB6]">
              <p className="text-[10px] uppercase font-bold text-[#0A6B2E]">Criadas</p>
              <p className="text-2xl font-bold text-[#0A6B2E]">{created.length}</p>
            </div>
            <div className="p-3 bg-[#FFF7E6] rounded border border-[#F0D8A8]">
              <p className="text-[10px] uppercase font-bold text-[#7A5C18]">Ignoradas</p>
              <p className="text-2xl font-bold text-[#7A5C18]">{skipped.length}</p>
              <p className="text-[10px] text-[#8B7340]">já inscritas</p>
            </div>
            <div className="p-3 bg-[#FDE8E8] rounded border border-[#F5A5A5]">
              <p className="text-[10px] uppercase font-bold text-[#9B1C1C]">Erros</p>
              <p className="text-2xl font-bold text-[#9B1C1C]">{errors.length}</p>
            </div>
          </div>

          {warnings.length > 0 && (
            <div className="p-2 bg-[#FFF7E6] border border-[#F0D8A8] rounded">
              <p className="font-bold text-[#7A5C18] mb-1">Avisos:</p>
              <ul className="list-disc pl-4 text-[#8B7340]">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {errors.length > 0 && (
            <div>
              <p className="font-bold mb-1">Linhas com erro:</p>
              <table className="w-full text-[11px] border border-[#E6E1D6]">
                <thead className="bg-[#FAF8F2]">
                  <tr><th className="p-1 text-left">Linha</th><th className="p-1 text-left">Email</th><th className="p-1 text-left">Erro</th></tr>
                </thead>
                <tbody>
                  {errors.map((e, i) => (
                    <tr key={i} className="border-t border-[#E6E1D6]"><td className="p-1">{e.row || "—"}</td><td className="p-1">{e.email || "—"}</td><td className="p-1 text-[#9B1C1C]">{e.error}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {created.some((c) => c.unresolved_lookups) && (
            <div className="p-2 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
              <p className="font-bold text-[#1E40AF] mb-1">Campos não reconhecidos:</p>
              <p className="text-[#1E40AF]">Algumas linhas têm valores nas colunas de domínio (Habilitação, Profissão, etc.) que não correspondem exactamente à lista oficial. Foram criadas na mesma — completa manualmente na edição da ficha.</p>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-[#E6E1D6] flex justify-end">
          <button onClick={onClose} data-testid="import-excel-result-ok"
            className="text-xs px-4 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540]">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function TemplatesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState("");
  // Estado de colapso persistido no localStorage. Default = colapsado
  // (a secção raramente é alterada e ocupa muito espaço).
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("ihtp.cursos.templates.collapsed") !== "0"; }
    catch { return true; }
  });
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("ihtp.cursos.templates.collapsed", next ? "1" : "0"); } catch { /* localStorage indisponível */ }
      return next;
    });
  };

  const load = async () => {
    if (collapsed) return; // não carregar enquanto colapsado
    setLoading(true);
    try { const r = await adminApi.cursoTemplatesList(); setItems(r.data || []); }
    catch (e) { toast.error(e.message || "Erro a carregar templates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [collapsed]);

  const onUpload = async (tipo, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(tipo);
    try {
      const res = await adminApi.cursoTemplateUpload(tipo, file);
      const jinja = res?.jinja_conversion || {};
      if (jinja.error) {
        toast.warning(`Template gravado, mas a versão Jinja falhou: ${jinja.error}`);
      } else {
        const placeholders = jinja.placeholders || [];
        const missing = jinja.missing || [];
        const unknown = jinja.unknown_fields || [];
        const phMsg = placeholders.length ? `${placeholders.length} placeholders activos` : "versão Jinja regenerada";
        toast.success(`Template actualizado — ${phMsg}.`);
        if (missing.length) toast.warning(`Secções não localizadas no template: ${missing.join(", ")}`);
        if (unknown.length) toast.warning(`MERGEFIELDs desconhecidos: ${unknown.join(", ")}`);
      }
      load();
    } catch (err) {
      toast.error(err.message || "Erro upload");
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  return (
    <section className="mb-6 p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid="cursos-templates-card">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="cursos-templates-toggle"
          aria-expanded={!collapsed}
          className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition"
          title={collapsed ? "Mostrar templates" : "Esconder templates"}
        >
          <span className="text-[#0F1829] text-sm" aria-hidden>
            {collapsed ? "▶" : "▼"}
          </span>
          <FileText size={16} className="text-[#0F1829]" />
          <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Templates de Documentos (CDN)</h2>
          {collapsed && items.length === 0 && (
            <span className="text-[11px] text-[#978D78] font-normal normal-case tracking-normal ml-1">
              (clique para abrir)
            </span>
          )}
        </button>
        {!collapsed && (
          <button onClick={load} className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1"
            data-testid="cursos-templates-reload">
            <RefreshCcw size={11} /> Recarregar
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="mt-3">
          {loading ? (
        <p className="text-xs text-[#6B7280] flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> A carregar...</p>
      ) : (
        <ul className="divide-y divide-[#F3F0E7]">
          {items.map((t) => {
            const isReportlab = t.engine === "reportlab";
            return (
            <li key={t.tipo} className="flex items-center gap-3 py-2.5" data-testid={`tpl-${t.tipo}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F1829] truncate flex items-center gap-2">
                  {t.label}
                  {isReportlab && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#0F1829] bg-[#FAF0CC] border border-[#E5D08A] px-1.5 py-0.5 rounded"
                      title="Gerado nativamente em Python (sem template Word editável)">
                      PDF · Reportlab
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-[#978D78] truncate">
                  {isReportlab ? (
                    "Gerador Python nativo — layout definido em código, sem ficheiro Word para editar."
                  ) : (
                    <>
                      {t.filename} {t.exists ? `• ${(t.size_bytes / 1024).toFixed(1)} KB • ${fmtDate(t.modified)}` : "• não disponível"}
                    </>
                  )}
                </p>
              </div>
              {t.exists && (
                <a href={t.preview_url} target="_blank" rel="noreferrer"
                  data-testid={`tpl-preview-${t.tipo}`}
                  title={isReportlab
                    ? "Pré-visualizar o PDF gerado com dados fictícios"
                    : "Renderizar com dados fictícios para validar placeholders"}
                  className="text-xs px-2.5 py-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1">
                  <Eye size={12} /> {isReportlab ? "Pré-visualizar PDF" : "Preview"}
                </a>
              )}
              {!isReportlab && t.exists && t.download_url && (
                <a href={t.download_url}
                  data-testid={`tpl-download-${t.tipo}`}
                  className="text-xs px-2.5 py-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1">
                  <FileDown size={12} /> Descarregar
                </a>
              )}
              {!isReportlab && (
                <label className="text-xs px-2.5 py-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] cursor-pointer inline-flex items-center gap-1"
                  data-testid={`tpl-upload-${t.tipo}`}>
                  {uploading === t.tipo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploading === t.tipo ? "A enviar..." : "Substituir"}
                  <input type="file" accept=".docx" className="hidden"
                    onChange={(e) => onUpload(t.tipo, e)} disabled={!!uploading} />
                </label>
              )}
            </li>
          );})}
        </ul>
      )}
      <p className="mt-3 text-[11px] text-[#6B7280] leading-relaxed">
        <strong>Ficha, Contrato e Declaração de Menor</strong> são agora gerados nativamente em Python (Reportlab) — não há ficheiro Word para editar. Para alterar layout ou texto legal, edita os serviços
        <code className="text-[10px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 mx-1 rounded">curso_ficha_inscricao_pdf.py</code>,
        <code className="text-[10px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 mx-1 rounded">curso_contrato_pdf.py</code> ou
        <code className="text-[10px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 mx-1 rounded">curso_menor_pdf.py</code>.
        O <strong>Diploma</strong> continua a usar o template Word Jinja (com MERGEFIELDs) — usa <strong>Substituir</strong> para carregar uma nova versão e <strong>Preview</strong> para validar os placeholders.
      </p>
        </div>
      )}
    </section>
  );
}

// ── Inscricao form (create/edit modal) ─────────────────────────────
// Field extraído para fora do `InscricaoForm` para evitar re-mount do <input>
// a cada keystroke (causava cursor jump — só 1 caractere de cada vez).
function Field({ label, children, span = 1 }) {
  return (
    <label className={`flex flex-col gap-1 text-xs ${span === 2 ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280]">{label}</span>
      {children}
    </label>
  );
}

function InscricaoForm({ cursoId, ficha, lookups, onClose, onSaved }) {
  const isEdit = Boolean(ficha?.ID_FichaInscricao);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState(() => ({
    ID_Cliente: ficha?.ID_Cliente ?? 0,
    NomeCompleto: ficha?.NomeCompleto || "",
    DataNascimento: (ficha?.DataNascimento || "").slice(0, 10),
    Morada: ficha?.Morada || "",
    CodigoPostal: ficha?.CodigoPostal || "",
    Localidade: ficha?.Localidade || "",
    ID_Nacionalidade: ficha?.ID_Nacionalidade || "PRT",
    DistritoNacionalidade: ficha?.DistritoNacionalidade || "",
    ConcelhoNacionalidade: ficha?.ConcelhoNacionalidade || "",
    NumeroSIGO: ficha?.NumeroSIGO || "",
    ID_TipoDocumento: ficha?.ID_TipoDocumento || "B",
    NumeroDocumento: ficha?.NumeroDocumento || "",
    DataValidadeDocumento: (ficha?.DataValidadeDocumento || "").slice(0, 10),
    Nif: ficha?.Nif || "",
    Email: ficha?.Email || "",
    Telefone: ficha?.Telefone || "",
    ID_Habilitacao: ficha?.ID_Habilitacao || "E",
    ID_TipoProfissao: ficha?.ID_TipoProfissao || "A",
    OutroTipoProfissao: ficha?.OutroTipoProfissao || "",
    Profissao: ficha?.Profissao || "",
    Empresa: ficha?.Empresa || "",
    MoradaEmpresa: ficha?.MoradaEmpresa || "",
    ID_SectorProfissionalEmpresa: ficha?.ID_SectorProfissionalEmpresa || "",
    ID_Referencia: ficha?.ID_Referencia || "C",
    OutraReferencia: ficha?.OutraReferencia || "",
    PaisOrigem: ficha?.PaisOrigem || "",
    AuthResidencia: ficha?.AuthResidencia || "",
    AuthResidenciaValidade: (ficha?.AuthResidenciaValidade || "").slice(0, 10),
    ConsentDGERT: ficha?.ConsentDGERT || "S",
    ConsentRegulamento: ficha?.ConsentRegulamento || "S",
    ConsentRegistoSite: ficha?.ConsentRegistoSite || "N",
    EncarregadoNome: ficha?.EncarregadoNome || "",
    EncarregadoCC: ficha?.EncarregadoCC || "",
    EncarregadoCCValidade: (ficha?.EncarregadoCCValidade || "").slice(0, 10),
    EncarregadoNif: ficha?.EncarregadoNif || "",
  }));

  // Em modo edição, a lista (`cursoInscricoesList`) só devolve um subset dos
  // campos. Para evitar mostrar campos vazios no modal apesar de a BD ter os
  // dados, vamos buscar a ficha completa via GET singular após abrir.
  useEffect(() => {
    if (!isEdit || !ficha?.ID_FichaInscricao) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.cursoInscricaoGet(cursoId, ficha.ID_FichaInscricao);
        const full = r?.data || {};
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          NomeCompleto: full.NomeCompleto ?? prev.NomeCompleto,
          DataNascimento: (full.DataNascimento || "").slice(0, 10) || prev.DataNascimento,
          Morada: full.Morada ?? prev.Morada,
          CodigoPostal: full.CodigoPostal ?? prev.CodigoPostal,
          Localidade: full.Localidade ?? prev.Localidade,
          ID_Nacionalidade: full.ID_Nacionalidade ?? prev.ID_Nacionalidade,
          DistritoNacionalidade: full.DistritoNacionalidade ?? prev.DistritoNacionalidade,
          ConcelhoNacionalidade: full.ConcelhoNacionalidade ?? prev.ConcelhoNacionalidade,
          NumeroSIGO: full.NumeroSIGO ?? prev.NumeroSIGO,
          ID_TipoDocumento: full.ID_TipoDocumento ?? prev.ID_TipoDocumento,
          NumeroDocumento: full.NumeroDocumento ?? prev.NumeroDocumento,
          DataValidadeDocumento: (full.DataValidadeDocumento || "").slice(0, 10) || prev.DataValidadeDocumento,
          Nif: full.Nif ?? prev.Nif,
          Email: full.Email ?? prev.Email,
          Telefone: full.Telefone ?? prev.Telefone,
          ID_Habilitacao: full.ID_Habilitacao ?? prev.ID_Habilitacao,
          ID_TipoProfissao: full.ID_TipoProfissao ?? prev.ID_TipoProfissao,
          OutroTipoProfissao: full.OutroTipoProfissao ?? prev.OutroTipoProfissao,
          Profissao: full.Profissao ?? prev.Profissao,
          Empresa: full.Empresa ?? prev.Empresa,
          MoradaEmpresa: full.MoradaEmpresa ?? prev.MoradaEmpresa,
          ID_SectorProfissionalEmpresa: full.ID_SectorProfissionalEmpresa ?? prev.ID_SectorProfissionalEmpresa,
          ID_Referencia: full.ID_Referencia ?? prev.ID_Referencia,
          OutraReferencia: full.OutraReferencia ?? prev.OutraReferencia,
          PaisOrigem: full.PaisOrigem ?? prev.PaisOrigem,
          AuthResidencia: full.AuthResidencia ?? prev.AuthResidencia,
          AuthResidenciaValidade: (full.AuthResidenciaValidade || "").slice(0, 10) || prev.AuthResidenciaValidade,
          ConsentDGERT: full.ConsentDGERT ?? prev.ConsentDGERT,
          ConsentRegulamento: full.ConsentRegulamento ?? prev.ConsentRegulamento,
          ConsentRegistoSite: full.ConsentRegistoSite ?? prev.ConsentRegistoSite,
          EncarregadoNome: full.EncarregadoNome ?? prev.EncarregadoNome,
          EncarregadoCC: full.EncarregadoCC ?? prev.EncarregadoCC,
          EncarregadoCCValidade: (full.EncarregadoCCValidade || "").slice(0, 10) || prev.EncarregadoCCValidade,
          EncarregadoNif: full.EncarregadoNif ?? prev.EncarregadoNif,
        }));
      } catch (err) {
        toast.error("Falha a carregar ficha completa: " + (err?.message || "erro"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, cursoId, ficha?.ID_FichaInscricao]);
  const [externo, setExterno] = useState(false);
  const [recoEmail, setRecoEmail] = useState("");
  const [recoStatus, setRecoStatus] = useState({ state: "idle", data: null }); // idle|loading|ok|missing|inactive|error
  const [encId, setEncId] = useState("");
  const [encStatus, setEncStatus] = useState({ state: "idle", data: null });   // idle|loading|ok|missing|error
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupEncomenda = async () => {
    const v = (encId || "").toString().trim();
    if (!v) { setEncStatus({ state: "idle", data: null }); return { state: "idle", data: null }; }
    if (!/^\d+$/.test(v)) {
      const next = { state: "missing", data: null };
      setEncStatus(next); return next;
    }
    setEncStatus({ state: "loading", data: null });
    try {
      const r = await adminApi.cursoEncomendaLookup(v);
      const next = r.found ? { state: "ok", data: r } : { state: "missing", data: null };
      setEncStatus(next); return next;
    } catch {
      const next = { state: "error", data: null };
      setEncStatus(next); return next;
    }
  };

  const lookupRecomendador = async () => {
    const e = recoEmail.trim().toLowerCase();
    if (!e) {
      setRecoStatus({ state: "idle", data: null });
      return { state: "idle", data: null };
    }
    setRecoStatus({ state: "loading", data: null });
    try {
      const r = await adminApi.cursoClienteLookup({ email: e });
      let next;
      if (!r.found) next = { state: "missing", data: null };
      else if ((r.Activo || "").toUpperCase() !== "S") next = { state: "inactive", data: r };
      else next = { state: "ok", data: r };
      setRecoStatus(next);
      return next;
    } catch (err) {
      const next = { state: "error", data: null };
      setRecoStatus(next);
      return next;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.Email) {
      const ev = await verifyEmail(form.Email);
      if (!ev.valid) {
        toast.error(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.cursoInscricaoUpdate(cursoId, ficha.ID_FichaInscricao, form);
        toast.success("Inscrição actualizada");
      } else {
        const reco = recoEmail.trim().toLowerCase();
        const encTrimmed = (encId || "").toString().trim();
        // Se o utilizador clicou em Criar sem ter saído do campo, o `onBlur`
        // não chegou a disparar (ou está a meio do lookup). Garantimos aqui.
        let status = recoStatus;
        if (externo && reco && (status.state === "idle" || status.state === "loading")) {
          status = await lookupRecomendador();
        }
        if (externo && reco && status.state !== "ok") {
          const msg =
            status.state === "inactive"
              ? "O cliente recomendador está inactivo. Indique um cliente activo ou deixe o campo vazio."
              : status.state === "missing"
              ? "Não foi encontrado cliente activo com esse email. Corrija ou deixe vazio."
              : "Valide o email do recomendador antes de gravar.";
          toast.error(msg);
          setSaving(false); return;
        }
        let encS = encStatus;
        // Validação do nº de encomenda vale sempre (independente de externo)
        if (encTrimmed && (encS.state === "idle" || encS.state === "loading")) {
          encS = await lookupEncomenda();
        }
        if (encTrimmed && encS.state !== "ok") {
          toast.error(
            encS.state === "missing"
              ? `Encomenda ${encTrimmed} não encontrada.`
              : "Valide o nº de encomenda antes de gravar."
          );
          setSaving(false); return;
        }
        const hasEncomenda = !!(encTrimmed && encS.state === "ok");
        const payload = externo
          ? {
              ...form,
              _external_formando: true,
              _email_recomendacao: reco || undefined,
              _id_encomenda: hasEncomenda ? Number(encTrimmed) : undefined,
            }
          : {
              ...form,
              // Modo "cliente IHTP": só enviamos _id_encomenda quando indicado —
              // permite ao admin associar a ficha à encomenda do comprador
              // (ex: Patrícia comprou 6 vagas + admin adiciona +2 formandos
              // IHTP em nome dela → link 62 vai de 6 → 8 vagas).
              _id_encomenda: hasEncomenda ? Number(encTrimmed) : undefined,
            };
        const resp = await adminApi.cursoInscricaoCreate(cursoId, payload);
        if (resp?.mode === "incremented") {
          toast.success(resp.message || `Email já inscrito — +1 vaga adicionada ao link (total: ${resp.vagas_total}).`);
        } else if (resp?.mode === "duplicate_no_link") {
          toast.info(resp.message || "Email já tem inscrição neste curso (sem link associado — não foi feita alteração).");
        } else if (resp?.link_id && resp?.vagas_total) {
          // Nova ficha associada a link existente/novo (com _id_encomenda)
          toast.success(
            resp.message || `Inscrição criada — vinculada ao link #${resp.link_id} (vagas totais: ${resp.vagas_total}).`
          );
        } else {
          const encName = encS.data?.NomeCompleto;
          toast.success(
            externo
              ? (encName
                  ? `Inscrição criada — vinculada à encomenda ${encTrimmed} (${encName})`
                  : reco
                    ? `Inscrição criada — cliente vinculado a ${recoStatus.data?.NomeCompleto}`
                    : "Inscrição criada (cliente externo)")
              : "Inscrição criada"
          );
        }
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Erro a gravar");
    } finally { setSaving(false); }
  };

  const cls = "px-2.5 py-1.5 text-sm border border-[#E6E1D6] rounded bg-white focus:outline-none focus:border-[#0F1829]";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="inscricao-modal">
      <form onSubmit={submit}
        className="bg-white border border-[#E6E1D6] rounded-md w-full max-w-3xl my-8">
        <header className="flex items-center justify-between px-5 py-3 border-b border-[#E6E1D6]">
          <h3 className="text-sm font-bold uppercase tracking-[0.12em]">
            {isEdit ? "Editar Inscrição" : "Nova Inscrição"}
          </h3>
          <button type="button" onClick={onClose}
            className="text-[#6B7280] hover:text-[#0F1829]" data-testid="inscricao-modal-close">
            <X size={16} />
          </button>
        </header>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto relative">
          {loading && (
            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center"
              data-testid="inscricao-modal-loading">
              <Loader2 size={20} className="animate-spin text-[#0F1829]" />
            </div>
          )}
          <Field label="Nome completo *" span={2}>
            <input className={cls} required value={form.NomeCompleto} onChange={(e) => upd("NomeCompleto", e.target.value)}
              data-testid="inp-NomeCompleto" />
          </Field>
          <Field label="Email *">
            <input type="email" className={cls} required value={form.Email} onChange={(e) => upd("Email", e.target.value)} />
          </Field>
          <Field label="Telefone">
            <input className={cls} value={form.Telefone} onChange={(e) => upd("Telefone", e.target.value)} />
          </Field>
          <Field label="NIF">
            <input className={cls} value={form.Nif} onChange={(e) => upd("Nif", e.target.value)} />
          </Field>
          <Field label="Data de Nascimento">
            <input type="date" className={cls} value={form.DataNascimento} onChange={(e) => upd("DataNascimento", e.target.value)} />
          </Field>
          <Field label="Morada" span={2}>
            <input className={cls} value={form.Morada} onChange={(e) => upd("Morada", e.target.value)} />
          </Field>
          <Field label="Código Postal">
            <input className={cls} value={form.CodigoPostal} onChange={(e) => upd("CodigoPostal", e.target.value)} />
          </Field>
          <Field label="Localidade">
            <input className={cls} value={form.Localidade} onChange={(e) => upd("Localidade", e.target.value)} />
          </Field>
          <Field label="Nacionalidade">
            <select className={cls} value={form.ID_Nacionalidade} onChange={(e) => upd("ID_Nacionalidade", e.target.value)}>
              <option value="">—</option>
              {(lookups.nacionalidade || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="País de origem">
            <input className={cls} value={form.PaisOrigem} onChange={(e) => upd("PaisOrigem", e.target.value)} />
          </Field>
          <Field label="Distrito">
            <input className={cls} value={form.DistritoNacionalidade} onChange={(e) => upd("DistritoNacionalidade", e.target.value)} />
          </Field>
          <Field label="Concelho">
            <input className={cls} value={form.ConcelhoNacionalidade} onChange={(e) => upd("ConcelhoNacionalidade", e.target.value)} />
          </Field>
          <Field label="Tipo de Documento">
            <select className={cls} value={form.ID_TipoDocumento} onChange={(e) => upd("ID_TipoDocumento", e.target.value)}>
              {(lookups.tipo_documento || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Nº Documento">
            <input className={cls} value={form.NumeroDocumento} onChange={(e) => upd("NumeroDocumento", e.target.value)} />
          </Field>
          <Field label="Validade Doc.">
            <input type="date" className={cls} value={form.DataValidadeDocumento} onChange={(e) => upd("DataValidadeDocumento", e.target.value)} />
          </Field>
          <Field label="Nº SIGO">
            <input className={cls} value={form.NumeroSIGO} onChange={(e) => upd("NumeroSIGO", e.target.value)} />
          </Field>
          <Field label="Habilitação">
            <select className={cls} value={form.ID_Habilitacao} onChange={(e) => upd("ID_Habilitacao", e.target.value)}>
              {(lookups.habilitacao || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Condição perante o trabalho">
            <select className={cls} value={form.ID_TipoProfissao} onChange={(e) => upd("ID_TipoProfissao", e.target.value)}>
              {(lookups.tipo_profissao || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Profissão">
            <input className={cls} value={form.Profissao} onChange={(e) => upd("Profissao", e.target.value)} />
          </Field>
          <Field label="Empresa" span={2}>
            <input className={cls} value={form.Empresa} onChange={(e) => upd("Empresa", e.target.value)} />
          </Field>
          <Field label="Morada da empresa" span={2}>
            <input className={cls} value={form.MoradaEmpresa} onChange={(e) => upd("MoradaEmpresa", e.target.value)} />
          </Field>
          <Field label="Sector profissional">
            <select className={cls} value={form.ID_SectorProfissionalEmpresa} onChange={(e) => upd("ID_SectorProfissionalEmpresa", e.target.value)}>
              <option value="">—</option>
              {(lookups.sector || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Como teve conhecimento?">
            <select className={cls} value={form.ID_Referencia} onChange={(e) => upd("ID_Referencia", e.target.value)}>
              {(lookups.referencia || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Autorização de residência (S/N)">
            <select className={cls} value={form.AuthResidencia} onChange={(e) => upd("AuthResidencia", e.target.value)}>
              <option value="">—</option>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </Field>
          <Field label="Validade autorização">
            <input type="date" className={cls} value={form.AuthResidenciaValidade} onChange={(e) => upd("AuthResidenciaValidade", e.target.value)} />
          </Field>
          <Field label="Consente DGERT (S/N)">
            <select className={cls} value={form.ConsentDGERT} onChange={(e) => upd("ConsentDGERT", e.target.value)}>
              <option value="S">Sim</option><option value="N">Não</option>
            </select>
          </Field>
          <Field label="Consente Regulamento (S/N)">
            <select className={cls} value={form.ConsentRegulamento} onChange={(e) => upd("ConsentRegulamento", e.target.value)}>
              <option value="S">Sim</option><option value="N">Não</option>
            </select>
          </Field>
          <Field label="Consente registo no site (S/N)">
            <select className={cls} value={form.ConsentRegistoSite} onChange={(e) => upd("ConsentRegistoSite", e.target.value)}>
              <option value="S">Sim</option><option value="N">Não</option>
            </select>
          </Field>
          <div className="sm:col-span-2 mt-2 pt-3 border-t border-[#E6E1D6]">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#0F1829] mb-2">
              Encarregado de Educação <span className="font-normal normal-case text-[#6B7280]">(obrigatório se formando &lt; 18 anos)</span>
            </h4>
          </div>
          <Field label="Nome do Encarregado" span={2}>
            <input className={cls} value={form.EncarregadoNome} onChange={(e) => upd("EncarregadoNome", e.target.value)}
              data-testid="inp-EncarregadoNome" />
          </Field>
          <Field label="Nº CC do Encarregado">
            <input className={cls} value={form.EncarregadoCC} onChange={(e) => upd("EncarregadoCC", e.target.value)} />
          </Field>
          <Field label="Validade CC do Encarregado">
            <input type="date" className={cls} value={form.EncarregadoCCValidade} onChange={(e) => upd("EncarregadoCCValidade", e.target.value)} />
          </Field>
          <Field label="NIF do Encarregado">
            <input className={cls} value={form.EncarregadoNif} onChange={(e) => upd("EncarregadoNif", e.target.value)} />
          </Field>
        </div>

        <footer className="flex flex-col gap-2 px-5 py-3 border-t border-[#E6E1D6]">
          {!isEdit && (
            <div className="flex flex-col gap-2 mr-auto w-full">
              <label className="flex items-center gap-2 text-xs text-[#0F1829] cursor-pointer"
                data-testid="inscricao-externo-toggle">
                <input type="checkbox" checked={externo} onChange={(e) => setExterno(e.target.checked)}
                  className="rounded border-[#E6E1D6]" />
                <span>Formando externo (sem compra) — cria cliente automaticamente</span>
              </label>
              {externo && (
                <div className="flex items-center gap-2 text-xs pl-6">
                  <span className="text-[#6B7280] w-44">Email do recomendador:</span>
                  <input type="email" value={recoEmail}
                    onChange={(e) => { setRecoEmail(e.target.value); setRecoStatus({ state: "idle", data: null }); }}
                    onBlur={lookupRecomendador}
                    placeholder="opcional — quem trouxe o formando"
                    data-testid="inp-recoEmail"
                    className="flex-1 px-2 py-1 border border-[#E6E1D6] rounded text-xs" />
                  {recoStatus.state === "loading" && <Loader2 size={12} className="animate-spin text-[#6B7280]" />}
                  {recoStatus.state === "ok" && (
                    <span className="text-emerald-700 inline-flex items-center gap-1 text-[11px]"
                      data-testid="reco-ok">
                      <CheckCircle2 size={11} /> {recoStatus.data?.NomeCompleto}
                    </span>
                  )}
                  {recoStatus.state === "missing" && (
                    <span className="text-red-600 text-[11px]" data-testid="reco-missing">Não encontrado</span>
                  )}
                  {recoStatus.state === "inactive" && (
                    <span className="text-red-600 text-[11px]" data-testid="reco-inactive">
                      Cliente inactivo — não pode ser recomendador
                    </span>
                  )}
                  {recoStatus.state === "error" && (
                    <span className="text-red-600 text-[11px]" data-testid="reco-error">Erro ao validar</span>
                  )}
                </div>
              )}
              {/* Nº Encomenda: sempre disponível (não apenas em modo externo) —
                  permite associar a ficha a uma encomenda existente e
                  incrementar Quantidade do link (ex: comprador adicionou
                  mais formandos depois da compra). */}
              <div className="flex items-center gap-2 text-xs pl-6">
                <span className="text-[#6B7280] w-44">Nº Encomenda:</span>
                <input type="text" inputMode="numeric" value={encId}
                  onChange={(e) => { setEncId(e.target.value); setEncStatus({ state: "idle", data: null }); }}
                  onBlur={lookupEncomenda}
                  placeholder="opcional — associa à encomenda + incrementa vagas"
                  data-testid="inp-encId"
                  className="flex-1 px-2 py-1 border border-[#E6E1D6] rounded text-xs" />
                {encStatus.state === "loading" && <Loader2 size={12} className="animate-spin text-[#6B7280]" />}
                {encStatus.state === "ok" && (
                  <span className="text-emerald-700 inline-flex items-center gap-1 text-[11px]"
                    data-testid="enc-ok">
                    <CheckCircle2 size={11} /> {encStatus.data?.NomeCompleto} · {encStatus.data?.Estado || "—"}
                  </span>
                )}
                {encStatus.state === "missing" && (
                  <span className="text-red-600 text-[11px]" data-testid="enc-missing">Encomenda não encontrada</span>
                )}
                {encStatus.state === "error" && (
                  <span className="text-red-600 text-[11px]" data-testid="enc-error">Erro ao validar</span>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose}
              className="text-xs px-3 py-1.5 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]">Cancelar</button>
            <button type="submit" disabled={saving}
              data-testid="inscricao-save"
              className="text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540] inline-flex items-center gap-1.5 disabled:opacity-50">
              {saving && <Loader2 size={12} className="animate-spin" />}
              {isEdit ? "Guardar" : "Criar"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}

// ── Tab "Compras do produto" ───────────────────────────────────────
function ComprasTab({ compras, loading, cursoLabel, cursoId, inscricoes, onReload, onInscricoesChanged, onEditFicha, onDeleteFicha }) {
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // Set<ID_Cliente>
  const [importing, setImporting] = useState(false);
  const [sendEmailOnInscrever, setSendEmailOnInscrever] = useState(true);

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (compras || []).filter((c) => {
      if (onlyMissing && c.AlreadyEnrolled) return false;
      if (!term) return true;
      return (
        (c.NomeCompleto || "").toLowerCase().includes(term) ||
        (c.Email || "").toLowerCase().includes(term) ||
        String(c.Nif || "").includes(term) ||
        String(c.ID_Encomenda || "").includes(term)
      );
    });
  }, [compras, q, onlyMissing]);

  const counts = useMemo(() => {
    const all = compras || [];
    const enrolled = all.filter((c) => c.AlreadyEnrolled).length;
    const sumQty = (arr) => arr.reduce((acc, c) => acc + (Number(c.Quantidade) || 1), 0);
    return {
      total: all.length,
      enrolled,
      missing: all.length - enrolled,
      pessoasTotal: sumQty(all),
      pessoasInscritas: sumQty(all.filter((c) => c.AlreadyEnrolled)),
      pessoasEmFalta: sumQty(all.filter((c) => !c.AlreadyEnrolled)),
    };
  }, [compras]);

  // Apenas linhas "sem inscrição" são seleccionáveis. ID_Cliente é a chave
  // (dedup natural — mesma pessoa com várias encomendas só conta 1x).
  const selectableIds = useMemo(
    () => list.filter((c) => !c.AlreadyEnrolled).map((c) => c.ID_Cliente),
    [list]
  );
  const selectableSet = useMemo(() => new Set(selectableIds), [selectableIds]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = !allSelected && selectableIds.some((id) => selected.has(id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const toggleOne = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const importItems = async (items) => {
    if (!items.length) return;
    setImporting(true);
    try {
      const res = await adminApi.cursoBulkFromCompras(cursoId, items, sendEmailOnInscrever);
      const created = res?.created?.length || 0;
      const skipped = res?.skipped?.length || 0;
      const errors = res?.errors?.length || 0;
      const emails = res?.emails_sent || 0;
      if (created) {
        const suffix = sendEmailOnInscrever
          ? ` — ${emails}/${created} email${emails === 1 ? "" : "s"} enviado${emails === 1 ? "" : "s"}`
          : "";
        toast.success(`${created} inscriç${created === 1 ? "ão criada" : "ões criadas"}${suffix}.`);
      }
      if (skipped) toast.message(`${skipped} já inscritos — ignorados.`);
      if (errors) toast.error(`${errors} erros — ver consola.`);
      if (errors) console.error("bulk-from-compras errors:", res.errors);
      setSelected(new Set());
      onReload();
      if (onInscricoesChanged) onInscricoesChanged();
    } catch (e) {
      toast.error(e.message || "Erro a importar inscrições");
    } finally {
      setImporting(false);
    }
  };

  const handleImportSelected = () => {
    // 1 item por ID_Cliente seleccionado (usa a 1ª compra correspondente).
    const byClient = new Map();
    (compras || []).forEach((c) => {
      if (selected.has(c.ID_Cliente) && !byClient.has(c.ID_Cliente)) {
        byClient.set(c.ID_Cliente, { ID_Cliente: c.ID_Cliente, ID_Encomenda: c.ID_Encomenda });
      }
    });
    const items = Array.from(byClient.values());
    if (!items.length) {
      toast.error("Seleccione pelo menos uma compra sem inscrição.");
      return;
    }
    const emailNote = sendEmailOnInscrever
      ? "\n\n📧 Será enviado automaticamente o email com o link público pré-preenchido para cada formando."
      : "\n\n⚠️ Email NÃO será enviado (checkbox desligada). Os formandos não recebem o link automaticamente.";
    if (!window.confirm(
      `Inscrever ${items.length} formando${items.length === 1 ? "" : "s"} no curso?\n\n` +
      `Os dados pedagógicos serão preenchidos com defaults (a completar via edição).` +
      emailNote
    )) return;
    importItems(items);
  };
  const handleImportOne = (compra) => {
    importItems([{ ID_Cliente: compra.ID_Cliente, ID_Encomenda: compra.ID_Encomenda }]);
  };

  return (
    <section className="p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid="compras-tab">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h2 className="text-sm font-bold uppercase tracking-[0.12em]">
          Compras do produto — {cursoLabel}
        </h2>
        <button
          type="button"
          onClick={onReload}
          title="Recarregar"
          className="p-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
          data-testid="compras-reload"
        >
          <RefreshCcw size={12} />
        </button>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={handleImportSelected}
            disabled={importing}
            data-testid="compras-bulk-import"
            className="text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540] inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {importing ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
            Inscrever seleccionados ({selected.size})
          </button>
        )}
        <label
          className="inline-flex items-center gap-1.5 text-[11px] text-[#0F1829] cursor-pointer select-none ml-1"
          data-testid="compras-send-email-toggle"
          title="Quando ligado, ao inscrever também gera o link público e envia automaticamente ao formando o email com o link pré-preenchido. Aplica-se à inscrição em massa e à inscrição linha-a-linha."
        >
          <input
            type="checkbox"
            checked={sendEmailOnInscrever}
            onChange={(e) => setSendEmailOnInscrever(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#0F1829]"
          />
          Enviar email
        </label>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrar nome/email/NIF/encomenda…"
          className="ml-auto px-2 py-1 text-xs border border-[#E6E1D6] rounded w-64"
          data-testid="compras-filter"
        />
      </div>
      <p className="text-[11px] text-[#6B7280] mb-3" data-testid="compras-summary">
        <strong className="text-[#0F1829]">{counts.pessoasTotal}</strong> pessoa{counts.pessoasTotal === 1 ? "" : "s"} {counts.pessoasTotal === 1 ? "adquiriu" : "adquiriram"} o produto em <strong className="text-[#0F1829]">{counts.total}</strong> encomenda{counts.total === 1 ? "" : "s"}
        {counts.pessoasInscritas > 0 && (
          <> · <span className="text-emerald-700">{counts.pessoasInscritas} já inscrita{counts.pessoasInscritas === 1 ? "" : "s"}</span></>
        )}
        {counts.pessoasEmFalta > 0 && (
          <> · <span className="text-amber-700">{counts.pessoasEmFalta} por inscrever</span></>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="compras-chips">
        {[
          { k: false, label: "Todas",            count: counts.total,    qty: counts.pessoasTotal,    tone: "neutral" },
          { k: true,  label: "Apenas sem inscrição", count: counts.missing, qty: counts.pessoasEmFalta, tone: "amber" },
        ].map(({ k, label, count, qty, tone }) => {
          const active = onlyMissing === k;
          const base = "px-2.5 py-1 text-[11px] rounded border inline-flex items-center gap-1.5 transition";
          const cls = active
            ? "bg-[#0F1829] text-white border-[#0F1829]"
            : tone === "amber"
              ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
              : "bg-white text-[#0F1829] border-[#E6E1D6] hover:bg-[#FAF8F2]";
          return (
            <button
              key={String(k)}
              type="button"
              onClick={() => setOnlyMissing(k)}
              data-testid={`compras-chip-${k ? "missing" : "all"}`}
              className={`${base} ${cls}`}
              title={`${count} encomenda${count === 1 ? "" : "s"} · ${qty} pessoa${qty === 1 ? "" : "s"}`}
            >
              {label}
              <span className={`text-[10px] font-bold ${active ? "opacity-90" : "opacity-70"}`}>{count}</span>
              <span className={`text-[9px] ${active ? "opacity-75" : "opacity-50"}`}>· {qty}p</span>
            </button>
          );
        })}
      </div>
      {loading ? (
        <p className="text-xs text-[#6B7280] flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> A carregar...
        </p>
      ) : list.length === 0 ? (
        <p className="text-xs text-[#978D78] italic" data-testid="compras-empty">
          {compras?.length === 0
            ? "Sem compras registadas do produto associado a este curso."
            : "Nenhuma compra corresponde aos filtros."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#6B7280] border-b border-[#E6E1D6]">
                <th className="py-2 pr-2 w-7">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    disabled={selectableIds.length === 0}
                    data-testid="compras-select-all"
                    title={selectableIds.length === 0 ? "Sem compras seleccionáveis" : "Seleccionar/Desseleccionar todos sem inscrição"}
                  />
                </th>
                <th className="py-2 pr-2">Encomenda</th>
                <th className="py-2 pr-2">Estado</th>
                <th className="py-2 pr-2">Data</th>
                <th className="py-2 pr-2">Cliente</th>
                <th className="py-2 pr-2">NIF</th>
                <th className="py-2 pr-2">Email</th>
                <th className="py-2 pr-2 text-right">Qtd</th>
                <th className="py-2 pr-2 text-right">Valor</th>
                <th className="py-2 pr-2">Pagamento</th>
                <th className="py-2 pr-2">Inscrito?</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const paid = c.ID_EstadoEncomenda === "EC" || c.ID_EstadoEncomenda === "PR";
                const canSelect = !c.AlreadyEnrolled && selectableSet.has(c.ID_Cliente);
                const isSelected = selected.has(c.ID_Cliente);
                return (
                  <tr key={`${c.ID_Encomenda}-${c.ID_Cliente}`} className={`border-b border-[#F3F0E7] ${isSelected ? "bg-[#FAF8F2]" : ""}`} data-testid={`compra-row-${c.ID_Encomenda}`}>
                    <td className="py-2 pr-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(c.ID_Cliente)}
                        disabled={!canSelect}
                        data-testid={`compra-check-${c.ID_Encomenda}`}
                        title={canSelect ? "" : "Já inscrito"}
                      />
                    </td>
                    <td className="py-2 pr-2 font-mono text-[12px]">#{c.ID_Encomenda}</td>
                    <td className="py-2 pr-2">
                      <span
                        className={`inline-block text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded border ${
                          paid
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}
                        title={c.EstadoEncomenda}
                      >
                        {c.ID_EstadoEncomenda}
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-[12px]">{fmtDate(c.DataEncomenda)}</td>
                    <td className="py-2 pr-2 font-medium">
                      <div>{c.NomeCompleto}</div>
                      <div className="text-[10px] text-[#978D78] font-mono">#{c.ID_Cliente}</div>
                    </td>
                    <td className="py-2 pr-2 font-mono text-[12px]">{c.Nif || "—"}</td>
                    <td className="py-2 pr-2 truncate max-w-[200px]">{c.Email || "—"}</td>
                    <td className="py-2 pr-2 text-right font-mono">{c.Quantidade ?? "—"}</td>
                    <td className="py-2 pr-2 text-right font-mono">
                      {c.PrecoFinal != null
                        ? new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(c.PrecoFinal)
                        : "—"}
                    </td>
                    <td className="py-2 pr-2 text-[11px] text-[#4B5563]">{c.TipoPagamento || c.ID_TipoPagamento || "—"}</td>
                    <td className="py-2 pr-2">
                      {c.AlreadyEnrolled ? (
                        <span
                          data-testid={`compra-enrolled-${c.ID_Encomenda}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                        >
                          <CheckCircle2 size={9} /> Inscrito
                        </span>
                      ) : (
                        <span
                          data-testid={`compra-not-enrolled-${c.ID_Encomenda}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Sem inscrição
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      {c.AlreadyEnrolled ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              // Encontra a ficha completa para abrir o form. Fallback: passa stub
                              // com ID_FichaInscricao para que o backend resolva.
                              const ficha = (inscricoes || []).find(
                                (i) => i.ID_FichaInscricao === c.ID_FichaInscricao
                              ) || { ID_FichaInscricao: c.ID_FichaInscricao, ID_Cliente: c.ID_Cliente };
                              onEditFicha(ficha);
                            }}
                            disabled={!c.ID_FichaInscricao}
                            data-testid={`compra-edit-${c.ID_Encomenda}`}
                            title="Editar ficha de inscrição"
                            className="text-[11px] px-2 py-1 border border-[#E6E1D6] text-[#0F1829] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1 disabled:opacity-50"
                          >
                            <Pencil size={10} /> Editar
                          </button>
                          {Array.isArray(c.validated_fichas) && c.validated_fichas.length > 0 && (
                            <CompraQrButton encomendaId={c.ID_Encomenda} fichas={c.validated_fichas} cursoNome={cursoLabel} />
                          )}
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const r = await adminApi.cursoMarcarInscrito(cursoId, c.ID_Encomenda);
                                const where = r.email_sent ? "✉ enviado para " + r.recipient : "⚠ falhou o envio";
                                 
                                alert(`Link público (qty=${r.quantidade}):\n${r.url}\n\n${where}`);
                              } catch (e) {
                                 
                                alert(`Erro: ${e.message || e}`);
                              }
                            }}
                            data-testid={`compra-marcar-inscrito-${c.ID_Encomenda}`}
                            title="Gerar token de inscrição + enviar email ao cliente"
                            className="text-[11px] px-2 py-1 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1"
                          >
                            ✉ Marcar Inscrito
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const ficha = (inscricoes || []).find(
                                (i) => i.ID_FichaInscricao === c.ID_FichaInscricao
                              ) || { ID_FichaInscricao: c.ID_FichaInscricao, NomeCompleto: c.NomeCompleto };
                              if (onDeleteFicha) onDeleteFicha(ficha);
                              // Recarrega compras para reflectir o estado.
                              setTimeout(onReload, 800);
                            }}
                            disabled={!c.ID_FichaInscricao}
                            data-testid={`compra-unenroll-${c.ID_Encomenda}`}
                            title="Anular inscrição"
                            className="text-[11px] px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50"
                          >
                            <Trash2 size={10} /> Desinscrever
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleImportOne(c)}
                          disabled={importing}
                          data-testid={`compra-import-${c.ID_Encomenda}`}
                          title="Inscrever este cliente"
                          className="text-[11px] px-2 py-1 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
                        >
                          <UserPlus size={10} /> Inscrever
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Course Health Widget ───────────────────────────────────────────
// Painel compacto com indicadores rápidos do estado do curso:
//   • dias até início (ou "em curso" / "terminado")
//   • % de fichas assinadas
//   • % de contratos assinados
//   • menores sem declaração (alerta)
const HEALTH_TONE_CLS = {
  ok: "text-emerald-700 bg-emerald-50 border-emerald-200",
  amber: "text-amber-800 bg-amber-50 border-amber-200",
  warn: "text-red-700 bg-red-50 border-red-200",
  neutral: "text-[#4B5563] bg-[#FAF8F2] border-[#E6E1D6]",
};

function HealthStat({ label, value, sub, tone = "neutral", testid }) {
  return (
    <div className={`px-3 py-2.5 border rounded ${HEALTH_TONE_CLS[tone] || ""}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.12em] opacity-70">{label}</p>
      <p className="text-xl font-bold leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-[11px] opacity-75 mt-0.5">{sub}</p>}
    </div>
  );
}

function CourseHealthCard({ curso, counts, total, expectedTotal, cursoId }) {
  const [sending, setSending] = useState(false);
  if (!curso) return null;

  // Denominador unificado (calculado no parent — ver `expectedTotal` em
  // AdminCursoInscricoes): compradores + formandos externos manuais.
  const target = Number(expectedTotal) || total || 0;
  const signedFichas = Math.max(0, total - counts.semFicha);
  const signedContratos = Math.max(0, total - counts.semContrato);
  const pct = (signed) => (target === 0 ? null : Math.round((signed / target) * 100));
  const fichaPct = pct(signedFichas);
  const contratoPct = pct(signedContratos);

  const handleRemind = async () => {
    if (!window.confirm(
      "Enviar email de lembrete a todos os pendentes deste curso?\n\n" +
      "Inclui: compradores cujos formandos ainda não preencheram a ficha,\n" +
      "e formandos já inscritos (via admin) com docs assinados em falta."
    )) return;
    setSending(true);
    try {
      const r = await adminApi.cursoEnviarLembretes(cursoId);
      const total = r.total_pendentes ?? 0;
      const parts = [];
      if (r.comprador_sent) parts.push(`${r.comprador_sent} aos compradores`);
      if (r.formando_sent) parts.push(`${r.formando_sent} aos formandos`);
      const detail = parts.length ? parts.join(" + ") : "0 emails";
      const msg =
        `${r.sent} email${r.sent === 1 ? "" : "s"} enviado${r.sent === 1 ? "" : "s"} (${detail}) · ` +
        `${total} pendente${total === 1 ? "" : "s"}` +
        (r.no_email ? ` · ${r.no_email} sem email` : "") +
        (r.errors?.length ? ` · ${r.errors.length} erro${r.errors.length === 1 ? "" : "s"}` : "");
      if (r.sent > 0) toast.success(msg);
      else if (total === 0) toast.message("Nada a enviar — sem inscrições em falta.");
      else toast.error(msg);
      if (r.errors?.length) console.error("enviar-lembretes errors:", r.errors);
    } catch (e) {
      toast.error(e.message || "Erro a enviar lembretes");
    } finally { setSending(false); }
  };

  // Dias até início
  let daysInfo = { label: "—", tone: "neutral" };
  if (curso.DataInicio) {
    const ini = new Date(curso.DataInicio);
    const fim = curso.DataFim ? new Date(curso.DataFim) : ini;
    const now = new Date();
    if (now > fim) daysInfo = { label: "Terminado", tone: "neutral" };
    else if (now >= ini) daysInfo = { label: "Em curso", tone: "ok" };
    else {
      const days = Math.ceil((ini - now) / (1000 * 60 * 60 * 24));
      const tone = days <= 3 ? "warn" : days <= 10 ? "amber" : "ok";
      daysInfo = { label: `${days} dia${days === 1 ? "" : "s"} para iniciar`, tone };
    }
  }

  return (
    <section
      className="mb-3 p-4 bg-white border border-[#E6E1D6] rounded-md"
      data-testid="curso-health-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Activity size={14} className="text-[#0F1829]" />
        <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Saúde do Curso</h2>
        <span className="ml-auto text-[11px] text-[#6B7280]" data-testid="health-total">
          {target > 0 && target !== total ? (
            <>{total} de {target} pessoa{target === 1 ? "" : "s"} inscrita{target === 1 ? "" : "s"}</>
          ) : (
            <>{total} inscriç{total === 1 ? "ão" : "ões"}</>
          )}
        </span>
        <button
          type="button"
          onClick={handleRemind}
          disabled={sending}
          data-testid="health-remind-pending"
          title="Enviar email a todos os compradores com formulário em falta"
          className="text-[11px] px-2.5 py-1 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition"
        >
          {sending ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
          {sending ? "A enviar…" : "Lembrar pendentes"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <HealthStat
          label="Estado"
          value={
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock size={16} /> {daysInfo.label}
            </span>
          }
          tone={daysInfo.tone}
          testid="health-stat-state"
        />
        <HealthStat
          label="Fichas assinadas"
          value={fichaPct === null ? "—" : `${fichaPct}%`}
          sub={target === 0 ? "Sem inscrições" : `${signedFichas} de ${target}`}
          tone={target === 0 ? "neutral" : fichaPct === 100 ? "ok" : fichaPct >= 50 ? "amber" : "warn"}
          testid="health-stat-fichas"
        />
        <HealthStat
          label="Contratos assinados"
          value={contratoPct === null ? "—" : `${contratoPct}%`}
          sub={target === 0 ? "Sem inscrições" : `${signedContratos} de ${target}`}
          tone={target === 0 ? "neutral" : contratoPct === 100 ? "ok" : contratoPct >= 50 ? "amber" : "warn"}
          testid="health-stat-contratos"
        />
        <HealthStat
          label="Menores sem decl."
          value={
            <span className="inline-flex items-center gap-1.5">
              {counts.menorSemDecl > 0 && <AlertTriangle size={16} />}
              {counts.menorSemDecl}
            </span>
          }
          tone={counts.menorSemDecl === 0 ? "ok" : "warn"}
          testid="health-stat-menores"
        />
      </div>
    </section>
  );
}

// ── QR Code button (compras) — mostra QR(s) das fichas validadas
// associadas a esta encomenda. Multi-vaga abre os vários QRs lado a lado.
function CompraQrButton({ encomendaId, fichas, cursoNome }) {
  const openQrWindow = () => {
    const rows = (fichas || [])
      .filter((f) => !!f.CodigoBarras)
      .map((f) => {
        const code = String(f.CodigoBarras).replace(/</g, "&lt;");
        const nome = String(f.NomeCompleto || "").replace(/</g, "&lt;");
        const qrUrl = adminApi.cursoCheckinQrUrl(f.CodigoBarras);
        return `
          <div class="label">
            <img class="qr" alt="QR" src="${qrUrl}" />
            <div class="info">
              <div class="name">${nome}</div>
              <div class="code">${code}</div>
            </div>
          </div>`;
      }).join("");
    const curso = String(cursoNome || "").replace(/</g, "&lt;");
    const html = `<!doctype html>
<html lang="pt"><head><meta charset="utf-8">
<title>QR Codes — Enc#${encomendaId}</title>
<style>
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 0; padding: 16px; color: #0F1829; background: #FAF8F2; }
  h1 { font-size: 14px; margin: 0 0 14px; }
  .label { display: flex; align-items: center; gap: 14px; border: 1px solid #E6E1D6; background: #fff; padding: 12px 16px; border-radius: 6px; margin-bottom: 10px; max-width: 420px; }
  .qr { width: 130px; height: 130px; flex-shrink: 0; }
  .info { font-size: 12px; line-height: 1.35; min-width: 0; }
  .name { font-weight: 700; font-size: 14px; margin-bottom: 6px; word-break: break-word; }
  .code { font-family: ui-monospace, Menlo, monospace; font-size: 11px; color: #6B7280; word-break: break-all; }
  .actions { margin-top: 14px; }
  button { padding: 8px 14px; font-size: 12px; cursor: pointer; border-radius: 4px; border: 1px solid #0F1829; background: #0F1829; color: #fff; }
  @media print { .actions { display: none; } body { padding: 0; background: #fff; } }
</style></head>
<body>
  <h1>QR Codes — Encomenda #${encomendaId} · ${curso}</h1>
  ${rows || '<p>Sem QR codes validados.</p>'}
  <div class="actions"><button onclick="window.print()">Imprimir</button></div>
</body></html>`;
    const w = window.open("", "_blank", "width=560,height=720");
    if (!w) { toast.error("Permite popups para ver o QR."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };
  return (
    <button
      type="button"
      onClick={openQrWindow}
      data-testid={`compra-ver-qr-${encomendaId}`}
      title={`Ver QR Code (${fichas.length} validad${fichas.length > 1 ? "as" : "a"})`}
      className="text-[11px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 inline-flex items-center gap-1"
    >
      <QrCode size={10} /> Ver QR{fichas.length > 1 ? `s (${fichas.length})` : ""}
    </button>
  );
}


// ── Print QR button — etiqueta imprimível com o QR de check-in ────
function PrintQrButton({ ficha, cursoNome }) {
  const code = ficha.CodigoBarras;
  if (!code || ficha.repetente) return null;
  const openPrint = () => {
    const qrUrl = adminApi.cursoCheckinQrUrl(code);
    const name = (ficha.NomeCompleto || "").replace(/</g, "&lt;");
    const curso = (cursoNome || "").replace(/</g, "&lt;");
    const html = `<!doctype html>
<html lang="pt">
<head>
<meta charset="utf-8">
<title>QR Check-in — ${name}</title>
<style>
  @page { size: 90mm 60mm; margin: 4mm; }
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 0; padding: 12px; color: #0F1829; }
  .label { display: flex; align-items: center; gap: 14px; border: 1px solid #E6E1D6; padding: 10px 14px; border-radius: 6px; max-width: 360px; }
  .qr { width: 110px; height: 110px; flex-shrink: 0; }
  .info { font-size: 11px; line-height: 1.35; min-width: 0; }
  .name { font-weight: 700; font-size: 13px; margin-bottom: 4px; word-break: break-word; }
  .curso { color: #6E6754; margin-bottom: 6px; }
  .code { font-family: ui-monospace, Menlo, monospace; font-size: 10px; color: #978D78; word-break: break-all; }
  .actions { margin-top: 14px; }
  button { padding: 8px 14px; font-size: 12px; cursor: pointer; border-radius: 4px; border: 1px solid #0F1829; background: #0F1829; color: #fff; }
  @media print { .actions { display: none; } body { padding: 0; } }
</style>
</head>
<body>
  <div class="label">
    <img class="qr" alt="QR check-in" src="${qrUrl}" />
    <div class="info">
      <div class="name">${name}</div>
      <div class="curso">${curso}</div>
      <div class="code">${code}</div>
    </div>
  </div>
  <div class="actions">
    <button onclick="window.print()">Imprimir</button>
  </div>
  <script>window.addEventListener("load", () => setTimeout(() => window.print(), 400));</script>
</body>
</html>`;
    const w = window.open("", "_blank", "width=480,height=380");
    if (!w) { toast.error("Permite popups para imprimir o QR."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };
  return (
    <button onClick={openPrint} title={`Imprimir QR de check-in (${code})`}
      data-testid={`insc-print-qr-${ficha.ID_FichaInscricao}`}
      className="p-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] text-[#0F1829]">
      <QrCode size={12} />
    </button>
  );
}


// ── Validar button (Sprint 1F) ────────────────────────────────────
function ValidarButton({ ficha, cursoId, onValidated }) {
  const [busy, setBusy] = useState(false);
  const validated = !!ficha.DataValidacao || !!ficha.CodigoBarras;
  const isRepetente = !!ficha.repetente;

  // Habilitação: precisa de ficha + contrato; menor precisa também.
  // Aceita upload OU confirmação manual de email como "entregue".
  const isMinor = ficha.DataNascimento && (new Date() - new Date(ficha.DataNascimento)) / 31536000000 < 18;
  const fichaOk = ficha.ficha_entregue ?? (!!ficha.FichaAssinadaPath || !!ficha.FichaConfirmadaEmailEm);
  const contratoOk = ficha.contrato_entregue ?? (!!ficha.ContratoAssinadoPath || !!ficha.ContratoConfirmadoEmailEm);
  const menorOk = ficha.menor_entregue ?? (!!ficha.MenorAssinadoPath || !!ficha.MenorConfirmadoEmailEm);
  const docsOk = fichaOk && contratoOk && (!isMinor || menorOk);

  // Repetentes não validam (não recebem QR, diploma nem avaliação).
  if (isRepetente) {
    return (
      <button
        disabled
        title="Repetente — não recebe código de barras / diploma"
        data-testid={`insc-validar-repetente-${ficha.ID_FichaInscricao}`}
        className="p-1 border border-[#E6E1D6] rounded text-[#978D78] cursor-not-allowed opacity-50">
        <Send size={12} />
      </button>
    );
  }

  const onClick = async () => {
    if (validated) {
      if (!window.confirm("Esta inscrição já foi validada. Reenviar email com o código existente?")) return;
    } else if (!docsOk) {
      toast.warning("Documentos em falta: Ficha + Contrato" + (isMinor ? " + Decl. Menor" : ""));
      return;
    } else if (!window.confirm("Validar inscrição e enviar email de boas-vindas com código de barras?")) {
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.cursoValidar(cursoId, ficha.ID_FichaInscricao);
      if (res.email_sent) toast.success(`Email enviado a ${res.recipient}. Código: ${res.codigo}`);
      else toast.warning(`Inscrição validada (código ${res.codigo}) mas email não foi enviado.`);
      onValidated?.();
    } catch (e) {
      toast.error(e.message || "Erro");
    } finally { setBusy(false); }
  };

  const Icon = validated ? CheckCircle2 : Send;
  return (
    <button
      onClick={onClick}
      disabled={busy || (!validated && !docsOk)}
      data-testid={`insc-validar-${ficha.ID_FichaInscricao}`}
      title={validated ? "Já validada — reenviar email" : (docsOk ? "Validar e enviar email" : "Faltam documentos")}
      className={`p-1 border rounded ${
        validated ? "border-[#A6DFB6] bg-[#E6F4EA] text-[#0A6B2E] hover:bg-[#D4ECDA]"
                  : docsOk ? "border-[#0F1829] bg-[#0F1829] text-white hover:bg-[#1A2540]"
                           : "border-[#E6E1D6] text-[#978D78] cursor-not-allowed"
      } disabled:opacity-50`}>
      {busy ? <Loader2 className="animate-spin" size={12} /> : <Icon size={12} />}
    </button>
  );
}

// ── Documentos Tab — Ficha de Presenças + Seguros do Curso ────────
function DocumentosTab({ cursoId }) {
  const [showSeguroEmail, setShowSeguroEmail] = useState(false);

  return (
    <div className="space-y-4" data-testid="documentos-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DocCard
          icon={ClipboardList}
          title="Ficha de Presenças"
          subtitle="Registo de presenças com 1 coluna por sessão para rubrica do formando."
          href={adminApi.cursoPresencasPdfUrl(cursoId)}
          testid="presencas"
        />
        <DocCard
          icon={AlertTriangle}
          title="Inscrições Incompletas"
          subtitle="Lista de formandos activos que ainda não entregaram todos os documentos (ficha, contrato, decl. menor). Mesmo cabeçalho da Ficha de Presenças para controlo no check-in."
          href={adminApi.cursoIncompletosPdfUrl(cursoId)}
          testid="incompletos"
        />
        <DocCard
          icon={ShieldCheck}
          title="Seguros do Curso"
          subtitle="Lista de formandos cobertos pela Apólice 1016254920 da Solfut / I Have the Power."
          href={adminApi.cursoSegurosPdfUrl(cursoId)}
          testid="seguros"
          secondaryAction={{
            label: "Enviar por email",
            icon: Send,
            onClick: () => setShowSeguroEmail(true),
            testid: "btn-doc-seguros-email",
          }}
        />
      </div>
      {showSeguroEmail && (
        <SegurosEmailModal cursoId={cursoId} onClose={() => setShowSeguroEmail(false)} />
      )}
    </div>
  );
}


function DocCard({ icon: Icon, title, subtitle, href, testid, secondaryAction }) {
  return (
    <div className="flex items-start gap-3 p-5 bg-white border border-[#E6E1D6] rounded-md hover:border-[#0F1829] hover:shadow-sm transition group">
      <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white group-hover:bg-[#A8843C] transition">
        <Icon size={22} />
      </div>
      <div className="flex-1">
        <h3 className="text-base font-bold text-[#0F1829] mb-1">{title}</h3>
        <p className="text-[12px] text-[#6E6754] leading-relaxed mb-3">{subtitle}</p>
        <div className="flex flex-wrap gap-2">
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            data-testid={`btn-doc-${testid}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[#0F1829] hover:bg-[#1B2A4B] px-3 py-1.5 rounded transition"
          >
            <FileDown size={12} /> Gerar PDF
          </a>
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              data-testid={secondaryAction.testid}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0F1829] bg-white border border-[#0F1829] hover:bg-[#FAF8F2] px-3 py-1.5 rounded transition"
            >
              <secondaryAction.icon size={12} /> {secondaryAction.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function SegurosEmailModal({ cursoId, onClose }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    adminApi.cursoSegurosRecipients(cursoId)
      .then((r) => {
        setTo((r.to || []).join(", "));
        setCc((r.cc || []).join(", "));
      })
      .catch((e) => toast.error(e.message || "Erro a carregar destinatários"))
      .finally(() => setLoading(false));
  }, [cursoId]);

  const handleSend = async () => {
    const toList = to.split(",").map((s) => s.trim()).filter(Boolean);
    const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
    if (!toList.length) { toast.error("Pelo menos 1 destinatário 'Para:' obrigatório"); return; }
    setSending(true);
    try {
      const r = await adminApi.cursoSegurosSendEmail(cursoId, {
        to: toList, cc: ccList, message: message || undefined,
      });
      toast.success(`Email enviado para ${r.to.length} destinatário(s)${r.cc.length ? ` (+${r.cc.length} em cc)` : ""}.`);
      onClose();
    } catch (e) {
      toast.error(e.message || "Erro a enviar email");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="seguros-email-modal">
      <div className="bg-white rounded-lg w-full max-w-[640px] max-h-[90vh] overflow-hidden flex flex-col">
        <header className="px-5 py-3 border-b border-[#E6E1D6] flex items-center justify-between">
          <h3 className="font-semibold text-[#1B1B1B] inline-flex items-center gap-2">
            <Send size={16} /> Enviar Seguros do Curso por Email
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-[#FAF8F2] rounded">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="text-center text-[#978D78] py-8">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar destinatários…
            </div>
          ) : (
            <>
              <EmailField label="Para (vírgula entre vários endereços)">
                <textarea
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm font-mono"
                  data-testid="field-email-to"
                />
              </EmailField>
              <EmailField label="Cc">
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm font-mono"
                  data-testid="field-email-cc"
                />
              </EmailField>
              <EmailField label="Mensagem adicional (opcional)">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  placeholder="Texto livre adicionado ao corpo do email…"
                  data-testid="field-email-message"
                />
              </EmailField>
              <p className="text-[11px] text-[#978D78]">
                O assunto e corpo do email são gerados automaticamente a partir do nome,
                edição, local e datas do curso. O PDF é gerado em tempo real no momento do envio.
              </p>
            </>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-[#E6E1D6] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="btn-email-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={loading || sending || !to.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A] disabled:opacity-50"
            data-testid="btn-email-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar email
          </button>
        </footer>
      </div>
    </div>
  );
}


function EmailField({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}


// ── Avaliação Tab (Sprint 1D) ─────────────────────────────────────
function AvaliacaoTab({ cursoId, cursoLabel }) {
  const [status, setStatus] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        adminApi.cursoAvaliacaoStatus(cursoId),
        adminApi.cursoAvaliacaoResumo(cursoId),
      ]);
      setStatus(s);
      setResumo(r);
    } catch (e) {
      toast.error(e.message || "Erro a carregar avaliação");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (cursoId) load();   }, [cursoId]);

  const onToggle = async () => {
    if (!status) return;
    setActing("toggle");
    try {
      if (status.closed) await adminApi.cursoAvaliacaoReabrir(cursoId);
      else await adminApi.cursoAvaliacaoFechar(cursoId);
      toast.success(status.closed ? "Avaliação reaberta" : "Avaliação fechada");
      load();
    } catch (e) {
      toast.error(e.message || "Erro");
    } finally { setActing(""); }
  };

  const onSendEmails = async (forceResend = false) => {
    const msg = forceResend
      ? "REENVIAR emails de avaliação a TODOS os formandos, incluindo os que já receberam?\n\nATENÇÃO: quem já tem token vai receber o email OUTRA VEZ."
      : "Enviar email de avaliação aos formandos que ainda NÃO receberam?";
    if (!window.confirm(msg)) return;
    setActing("emails");
    try {
      const res = await adminApi.cursoAvaliacaoEnviarEmails(cursoId, { force_resend: forceResend });
      const suffix = res.tokens_created ? ` (${res.tokens_created} tokens criados)` : "";
      if (res.sent === 0 && !forceResend) {
        toast.info("Todos os formandos já receberam o email — nada a enviar. Usa 'Reenviar a todos' se pretendes duplicar.");
      } else {
        toast.success(`${res.sent} emails enviados${suffix}${res.force_resend ? " (REENVIO forçado)" : ""}.`);
      }
      if (res.no_email) toast.warning(`${res.no_email} formandos sem email`);
      load();
    } catch (e) {
      toast.error(e.message || "Erro a enviar emails");
    } finally { setActing(""); }
  };

  if (loading) {
    return <p className="text-sm text-[#6B7280] flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> A carregar...</p>;
  }
  if (!status || !resumo) return null;

  const respRate = status.total_tokens ? Math.round((status.tokens_usados / status.total_tokens) * 100) : 0;

  return (
    <section className="space-y-4" data-testid="avaliacao-tab">
      {/* KPI strip + actions */}
      <div className="p-4 bg-white border border-[#E6E1D6] rounded-md">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Avaliação da Reação — {cursoLabel}</h2>
          <span data-testid="avaliacao-status-pill" className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full ${
            status.closed ? "bg-[#FDE8E8] text-[#9B1C1C]" : "bg-[#E6F4EA] text-[#0A6B2E]"
          }`}>{status.closed ? "Fechada" : "Aberta"}</span>
          <button onClick={load} className="ml-auto text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1" data-testid="avaliacao-reload">
            <RefreshCcw size={11} /> Recarregar
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Kpi label="Respostas" value={status.total_respostas} testid="avaliacao-kpi-respostas" />
          <Kpi label="Emails enviados" value={status.emails_sent} testid="avaliacao-kpi-emails" />
          <Kpi label="Taxa resposta" value={`${respRate}%`} sub={`${status.tokens_usados} / ${status.total_tokens}`} testid="avaliacao-kpi-taxa" />
          <Kpi label="Média global" value={resumo.overall_avg != null ? resumo.overall_avg.toFixed(2) : "—"} sub="(1-5)" testid="avaliacao-kpi-media" />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onSendEmails}
            disabled={!!acting || status.closed}
            data-testid="avaliacao-enviar-emails"
            className="text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540] disabled:opacity-50 inline-flex items-center gap-1.5">
            {acting === "emails" ? <Loader2 className="animate-spin" size={12} /> : <Mail size={12} />}
            Enviar emails aos formandos
          </button>
          <button
            onClick={onToggle}
            disabled={!!acting}
            data-testid="avaliacao-toggle"
            className={`text-xs px-3 py-1.5 rounded disabled:opacity-50 inline-flex items-center gap-1.5 ${
              status.closed ? "bg-[#E6F4EA] text-[#0A6B2E] hover:bg-[#D4ECDA]"
                            : "bg-[#FDE8E8] text-[#9B1C1C] hover:bg-[#F5C6CB]"
            }`}>
            {acting === "toggle" ? <Loader2 className="animate-spin" size={12} /> : <Activity size={12} />}
            {status.closed ? "Reabrir avaliação" : "Fechar avaliação"}
          </button>
        </div>
        {status.closed && (
          <p className="text-[11px] text-[#9B1C1C] mt-2">
            ⚠ Com a avaliação fechada, os links existentes mostram mensagem &quot;Avaliação fechada&quot; e novas submissões são bloqueadas.
          </p>
        )}
      </div>

      {/* Resumo por pergunta */}
      <div className="p-4 bg-white border border-[#E6E1D6] rounded-md">
        <h3 className="text-sm font-bold uppercase tracking-[0.12em] mb-3">Resumo por pergunta</h3>
        {resumo.total_responses === 0 ? (
          <p className="text-xs text-[#6B7280]">Ainda sem respostas.</p>
        ) : (
          <div className="space-y-4">
            {["curso", "formador"].map((sec) => {
              const qs = Object.entries(resumo.questions).filter(([, v]) => v.section === sec);
              return (
                <div key={sec}>
                  <h4 className="text-[11px] uppercase tracking-wider text-[#978D78] mb-2">
                    {sec === "curso" ? "Sobre o Curso" : "Sobre o Formador"}
                  </h4>
                  <ul className="space-y-2">
                    {qs.map(([key, v]) => (
                      <li key={key} data-testid={`avaliacao-resumo-${key}`} className="grid grid-cols-12 gap-2 items-center text-xs py-1.5 border-b border-[#F3F0E7] last:border-b-0">
                        <span className="col-span-5 text-[#0F1829]">{v.label}</span>
                        <span className="col-span-2 font-bold text-[#0F1829]">
                          {v.avg != null ? v.avg.toFixed(2) : "—"}
                          <span className="text-[10px] text-[#978D78] ml-1">({v.count})</span>
                        </span>
                        <div className="col-span-5 flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => {
                            const c = v.distribution[n] || 0;
                            const pct = v.count ? (c / v.count) * 100 : 0;
                            return (
                              <div key={n} title={`${n}: ${c} respostas (${pct.toFixed(0)}%)`} className="flex-1 h-5 rounded-sm bg-[#F3F0E7] relative overflow-hidden">
                                <div className="absolute inset-y-0 left-0 bg-[#0F1829]" style={{ width: `${pct}%` }} />
                                <span className="relative z-10 text-[9px] flex items-center justify-center h-full text-[#0F1829] mix-blend-difference">{n}</span>
                              </div>
                            );
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comentários livres */}
      <div className="p-4 bg-white border border-[#E6E1D6] rounded-md">
        <h3 className="text-sm font-bold uppercase tracking-[0.12em] mb-3">Sugestões / Críticas ({resumo.sugestoes?.length || 0})</h3>
        {(!resumo.sugestoes || resumo.sugestoes.length === 0) ? (
          <p className="text-xs text-[#6B7280]">Sem comentários submetidos.</p>
        ) : (
          <ul className="space-y-2">
            {resumo.sugestoes.map((s) => (
              <li key={s.id} className="p-2 bg-[#FAF8F2] border border-[#F3F0E7] rounded text-xs" data-testid={`avaliacao-sugestao-${s.id}`}>
                <p className="text-[#0F1829] whitespace-pre-wrap">{s.text}</p>
                <p className="text-[10px] text-[#978D78] mt-1">{s.data}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pointer para a nova página de Formadores (Mod. 16) */}
      <div className="p-4 bg-[#FAF8F2] border border-[#E6E1D6] rounded-md text-xs text-[#6E6754]">
        <p>
          <strong>Mod. 16 (Avaliação do Formador)</strong> foi movido para uma página dedicada:{" "}
          <Link to={`/admin/cursos/formadores?curso=${cursoId}`}
                className="text-[#A8843C] font-semibold hover:underline"
                data-testid="link-formadores-page">
            /admin/cursos/formadores?curso={cursoId}
          </Link>
          . Aí podes gerir a lista de formadores e enviar o link anónimo a cada um.
        </p>
      </div>
    </section>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="p-3 bg-[#FAF8F2] border border-[#F3F0E7] rounded" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#978D78]">{label}</p>
      <p className="text-xl font-bold text-[#0F1829]">{value}</p>
      {sub && <p className="text-[10px] text-[#6B7280]">{sub}</p>}
    </div>
  );
}


// ── Diplomas Tab (Sprint 1H) ──────────────────────────────────────
function DiplomasTab({ cursoId, inscricoes, onReload }) {
  const [busyAll, setBusyAll] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Helper: docs completos = Ficha + Contrato + (Menor se aplicável).
  // Alinhado com _pending_docs no backend (curso_incompletos_pdf.py).
  const docsCompletos = (i) => {
    if (!i.ficha_entregue) return false;
    if (!i.contrato_entregue) return false;
    if (i.is_menor && !i.menor_entregue) return false;
    return true;
  };

  // Repetentes não recebem diploma — excluídos do scope desta tab.
  const list = (inscricoes || []).filter((i) => !i.repetente);
  const stats = {
    total: list.length,
    enviados: list.filter((i) => i.diploma_enviado).length,
    semEmail: list.filter((i) => !(i.Email || "").trim()).length,
    docsIncompletos: list.filter((i) => !docsCompletos(i)).length,
  };

  const handleSendOne = async (ficha) => {
    if (!docsCompletos(ficha)) {
      toast.error("Documentação incompleta — não é possível enviar o diploma.");
      return;
    }
    setBusyId(ficha.ID_FichaInscricao);
    try {
      const res = await adminApi.diplomaEnviar(cursoId, ficha.ID_FichaInscricao);
      toast.success(`Diploma enviado para ${res.recipient}.`);
      onReload();
    } catch (e) {
      toast.error(e.message || "Erro a enviar diploma.");
    } finally { setBusyId(null); }
  };

  const handlePreviewOne = async (ficha) => {
    setBusyId(ficha.ID_FichaInscricao);
    try {
      const blob = await adminApi.diplomaPreview(cursoId, ficha.ID_FichaInscricao);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Libertar URL ao fechar a aba (5 min depois — suficiente para abrir)
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    } catch (e) {
      toast.error(e.message || "Erro a gerar diploma.");
    } finally { setBusyId(null); }
  };

  // ── DGERT — upload manual do PDF por formando (só admin) ────────────
  const [dgertBusyId, setDgertBusyId] = useState(null);
  const handleDgertUpload = async (ficha, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Apenas ficheiros PDF são aceites para o Diploma DGERT.");
      return;
    }
    setDgertBusyId(ficha.ID_FichaInscricao);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `/api/admin/cursos/${cursoId}/inscricoes/${ficha.ID_FichaInscricao}/dgert/upload`,
        { method: "POST", credentials: "include", body: fd },
      );
      if (!res.ok) throw new Error(await res.text());
      toast.success(`Diploma DGERT carregado para ${ficha.NomeCompleto.split(" ")[0]}.`);
      onReload();
    } catch (e) {
      toast.error(`Erro no upload DGERT: ${e.message || e}`);
    } finally {
      setDgertBusyId(null);
    }
  };
  const handleDgertDelete = async (ficha) => {
    if (!window.confirm(
      `Remover a referência ao Diploma DGERT de ${ficha.NomeCompleto}?\n\n` +
      `O ficheiro na CDN mantém-se, mas deixa de aparecer no MYIHTP.`
    )) return;
    setDgertBusyId(ficha.ID_FichaInscricao);
    try {
      const res = await fetch(
        `/api/admin/cursos/${cursoId}/inscricoes/${ficha.ID_FichaInscricao}/dgert`,
        { method: "DELETE", credentials: "include" },
      );
      if (!res.ok) throw new Error(await res.text());
      toast.success("Referência DGERT removida.");
      onReload();
    } catch (e) {
      toast.error(`Erro: ${e.message || e}`);
    } finally {
      setDgertBusyId(null);
    }
  };

  const handleSendAll = async () => {
    // Construir lista explícita de IDs (com email, sem diploma já enviado
    // e com **documentação completa** — regra oficial Jul/2026). Formandos
    // com docs em falta são explicitamente omitidos.
    const candidates = list
      .filter((i) => (i.Email || "").trim() && !i.diploma_enviado && docsCompletos(i))
      .map((i) => i.ID_FichaInscricao);
    if (candidates.length === 0) {
      toast.info("Não há formandos elegíveis (sem email, já enviados ou docs incompletos).");
      return;
    }
    if (!window.confirm(
      `Gerar e enviar diplomas a ${candidates.length} formando(s) do curso?\n\nIsto irá:\n• Gerar PDF (em tempo real, com data fim do curso)\n• Enviar por email com anexo\n• Marcar como "Diploma Enviado" na ficha\n\nProtecção: só é permitido após o curso terminar.`
    )) return;
    setBusyAll(true);
    try {
      const res = await adminApi.diplomaEnviarEmMassa(cursoId, {
        ficha_ids: candidates,
        confirm_token: `${candidates.length}:${cursoId}`,
      });
      // Novo comportamento: envio em background — resposta é 202-like.
      if (res.status === "processing") {
        toast.success(res.message || `${res.queued} diplomas em envio. Recebes email de resumo quando terminar.`, { duration: 8000 });
      } else if (res.sent) {
        // Fallback para resposta antiga (sync)
        toast.success(`Concluído: ${res.sent.length} enviados, ${res.failed?.length || 0} falhas.`);
        if (res.failed?.length) console.warn("Diploma bulk failures:", res.failed);
      } else {
        toast.success("Envio iniciado.");
      }
      onReload();
    } catch (e) {
      toast.error(e.message || "Erro no envio em massa.");
    } finally { setBusyAll(false); }
  };

  return (
    <section data-testid="diplomas-tab" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Inscritos" value={stats.total} testid="diplomas-kpi-total" />
        <Kpi label="Diplomas enviados" value={stats.enviados} sub={stats.total ? `${Math.round((stats.enviados / stats.total) * 100)}%` : null} testid="diplomas-kpi-enviados" />
        <Kpi label="Sem email" value={stats.semEmail} sub={stats.semEmail ? "Bloqueiam envio" : "Tudo OK"} testid="diplomas-kpi-sem-email" />
        <Kpi label="Docs incompletos" value={stats.docsIncompletos} sub={stats.docsIncompletos ? "Bloqueiam envio" : "Tudo OK"} testid="diplomas-kpi-docs-incompletos" />
      </div>

      <div className="flex flex-wrap items-center gap-2 p-3 bg-[#FAF8F2] border border-[#F3F0E7] rounded">
        <p className="text-xs text-[#6B7280] flex-1 min-w-[200px]">
          Diplomas Solfut gerados em tempo real (sem armazenamento) e enviados ao formando por email com anexo PDF.
          A <strong>data oficial</strong> usada é a <strong>data fim</strong> do curso — pode regenerar sempre que precisar.
        </p>
        <button
          type="button"
          onClick={handleSendAll}
          disabled={busyAll || stats.total === 0}
          data-testid="diplomas-bulk-send"
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded bg-[#0F1829] text-white hover:bg-black inline-flex items-center gap-2 disabled:opacity-50"
        >
          {busyAll ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {busyAll ? "A enviar…" : `Enviar a todos (${stats.total})`}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#6B7280] border-b border-[#E6E1D6]">
              <th className="py-2 pr-2">Nome</th>
              <th className="py-2 pr-2">Email</th>
              <th className="py-2 pr-2">Diploma IHTP</th>
              <th className="py-2 pr-2">DGERT</th>
              <th className="py-2 pr-2 text-right">Acções</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} className="py-3 text-xs text-[#978D78] italic">Sem inscrições.</td></tr>
            ) : list.map((i) => {
              const noEmail = !(i.Email || "").trim();
              const complete = docsCompletos(i);
              // Formandos com docs incompletos ficam desactivados aqui.
              // A tab "Documentos" mostra explicitamente o que falta a cada um.
              return (
                <tr
                  key={i.ID_FichaInscricao}
                  className={`border-b border-[#F3F0E7] ${!complete ? "opacity-60 bg-orange-50/30" : ""}`}
                  data-testid={`diploma-row-${i.ID_FichaInscricao}`}
                >
                  <td className="py-2 pr-2">
                    {i.NomeCompleto}
                    {i.repetente && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">A repetir</span>
                    )}
                    {!complete && (
                      <span
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200"
                        title="Não pode receber diploma até entregar toda a documentação (Ficha + Contrato + Menor se aplicável)."
                        data-testid={`diploma-row-docs-incompletos-${i.ID_FichaInscricao}`}
                      >
                        Docs incompletos
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-xs">{i.Email || <span className="text-red-500 italic">sem email</span>}</td>
                  <td className="py-2 pr-2 text-[11px]">
                    {i.diploma_enviado ? (
                      <span className="inline-flex flex-col items-start gap-0.5">
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 size={11} /> Enviado
                        </span>
                        {i.diploma_enviado_em && (
                          <span
                            className="text-[10px] text-[#6B7280] font-mono"
                            data-testid={`diploma-enviado-em-${i.ID_FichaInscricao}`}
                            title={i.diploma_enviado_para ? `Para: ${i.diploma_enviado_para}` : ""}
                          >
                            {fmtDateTime(i.diploma_enviado_em)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-[#6B7280]">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-[11px]">
                    {i.dgert_path ? (
                      <div className="inline-flex items-center gap-1">
                        <a
                          href={i.dgert_path.startsWith("cdn:") ? i.dgert_path.replace("cdn:", "https://doremon.ihavethepower.net/") : i.dgert_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                          title="Abrir PDF DGERT"
                          data-testid={`dgert-link-${i.ID_FichaInscricao}`}
                        >
                          <CheckCircle2 size={11} /> Carregado
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDgertDelete(i)}
                          disabled={dgertBusyId === i.ID_FichaInscricao}
                          className="text-[10px] px-1 py-0.5 text-red-600 hover:bg-red-50 rounded"
                          title="Remover referência DGERT (o ficheiro CDN mantém-se)"
                          data-testid={`dgert-delete-${i.ID_FichaInscricao}`}
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ) : (
                      <label
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-[#E6E1D6] rounded cursor-pointer hover:bg-[#FAF8F2] text-[#6B7280]"
                        title="Fazer upload do Diploma DGERT (PDF)"
                        data-testid={`dgert-upload-label-${i.ID_FichaInscricao}`}
                      >
                        {dgertBusyId === i.ID_FichaInscricao ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <Upload size={10} />
                        )}
                        {dgertBusyId === i.ID_FichaInscricao ? "A carregar…" : "Upload PDF"}
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          disabled={dgertBusyId === i.ID_FichaInscricao}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";  // permite re-upload do mesmo file
                            if (f) handleDgertUpload(i, f);
                          }}
                          data-testid={`dgert-upload-${i.ID_FichaInscricao}`}
                        />
                      </label>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        type="button"
                        onClick={() => handlePreviewOne(i)}
                        disabled={busyId === i.ID_FichaInscricao}
                        data-testid={`diploma-preview-${i.ID_FichaInscricao}`}
                        title="Pré-visualizar PDF (abre numa nova aba — não envia email)"
                        className="text-[11px] px-2 py-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        {busyId === i.ID_FichaInscricao ? <Loader2 size={10} className="animate-spin" /> : <Eye size={10} />}
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendOne(i)}
                        disabled={noEmail || !complete || busyId === i.ID_FichaInscricao}
                        data-testid={`diploma-enviar-${i.ID_FichaInscricao}`}
                        title={noEmail ? "Ficha sem email" : (!complete ? "Documentação incompleta — não pode enviar diploma" : "Gerar + enviar diploma por email")}
                        className="text-[11px] px-2 py-1 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        {busyId === i.ID_FichaInscricao ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
                        Enviar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


// ── CDN Files Tab — Inspector dos uploads em Bunny CDN ─────────────
// Lista os ficheiros físicos da pasta `clientes/cursos/<curso_id>/`,
// cruza com referências em BD e sinaliza órfãos (anexos abandonados).
const _fmtBytes = (n) => {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const _CDN_TIPO_LABEL = {
  ficha: { label: "Ficha", cls: "bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]" },
  contrato: { label: "Contrato", cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" },
  menor: { label: "Menor", cls: "bg-[#FEF3C7] text-[#854D0E] border-[#FCD34D]" },
};

function CdnFilesTab({ cursoId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | refed | orphan
  const [search, setSearch] = useState("");
  const [deletingName, setDeletingName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.cursoCdnFiles(cursoId);
      setData(r);
    } catch (e) {
      setError(e.message || "Erro a consultar a CDN.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cursoId]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === "orphan" && !it.orphan) return false;
      if (filter === "refed" && it.orphan) return false;
      if (q) {
        const hay = `${it.name} ${it.ficha_nome || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter, search]);

  const handleDeleteOrphan = async (name) => {
    if (!window.confirm(
      `Apagar o ficheiro órfão "${name}"?\n\nEsta operação é IRREVERSÍVEL e remove o ficheiro da Bunny CDN. Só é possível apagar ficheiros não referenciados por nenhuma ficha.`
    )) return;
    setDeletingName(name);
    try {
      await adminApi.cursoCdnFileDelete(cursoId, name);
      toast.success(`Ficheiro "${name}" apagado.`);
      load();
    } catch (e) {
      toast.error(e.message || "Erro a apagar ficheiro.");
    } finally {
      setDeletingName("");
    }
  };

  return (
    <section data-testid="cdn-tab" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Ficheiros" value={data?.count ?? "—"} testid="cdn-kpi-count" />
        <Kpi label="Tamanho total" value={_fmtBytes(data?.total_size_bytes || 0)} testid="cdn-kpi-size" />
        <Kpi label="Órfãos" value={data?.orphan_count ?? 0}
          sub={data?.orphan_count ? "Sem referência em BD" : "Tudo referenciado"}
          testid="cdn-kpi-orphans" />
        <Kpi label="Pasta" value={`clientes/cursos/${cursoId}`} testid="cdn-kpi-folder" />
      </div>

      <div className="flex flex-wrap items-center gap-2 p-3 bg-[#FAF8F2] border border-[#F3F0E7] rounded">
        <HardDrive size={14} className="text-[#0F1829]" />
        <p className="text-xs text-[#6B7280] flex-1 min-w-[200px]">
          Lista os ficheiros físicos na Bunny CDN para este curso e cruza com as fichas em BD.
          Os <strong>órfãos</strong> (sem referência) podem ser apagados em segurança.
        </p>
        <div className="inline-flex border border-[#E6E1D6] rounded overflow-hidden text-[11px]">
          {[
            { id: "all", label: "Todos" },
            { id: "refed", label: "Referenciados" },
            { id: "orphan", label: "Órfãos" },
          ].map((opt) => (
            <button key={opt.id} type="button"
              data-testid={`cdn-filter-${opt.id}`}
              onClick={() => setFilter(opt.id)}
              className={`px-3 py-1.5 ${filter === opt.id ? "bg-[#0F1829] text-white" : "bg-white text-[#0F1829] hover:bg-[#FAF8F2]"}`}>
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#978D78]" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="cdn-search"
            placeholder="Pesquisar nome/formando…"
            className="text-xs pl-7 pr-2 py-1.5 border border-[#E6E1D6] rounded w-56" />
        </div>
        <button type="button" onClick={load} disabled={loading}
          data-testid="cdn-reload"
          className="px-3 py-1.5 text-xs border border-[#E6E1D6] rounded hover:bg-white inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
          Recarregar
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start gap-2"
          data-testid="cdn-error">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && !data ? (
        <p className="text-xs text-[#6B7280] flex items-center gap-1.5 py-4">
          <Loader2 size={12} className="animate-spin" /> A consultar a CDN…
        </p>
      ) : (
        <div className="overflow-x-auto border border-[#E6E1D6] rounded">
          <table className="w-full text-sm" data-testid="cdn-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#6B7280] bg-[#FAF8F2] border-b border-[#E6E1D6]">
                <th className="py-2 px-3">Ficheiro</th>
                <th className="py-2 px-3">Formando</th>
                <th className="py-2 px-3">Tipo</th>
                <th className="py-2 px-3 text-right">Tamanho</th>
                <th className="py-2 px-3">Modificado</th>
                <th className="py-2 px-3 text-right">Acções</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="py-4 px-3 text-xs text-[#978D78] italic">
                  {items.length === 0 ? "A pasta da CDN está vazia." : "Nenhum ficheiro corresponde ao filtro."}
                </td></tr>
              ) : (
                filtered.map((it) => {
                  const tipoCfg = _CDN_TIPO_LABEL[it.tipo] || null;
                  return (
                    <tr key={it.name} className="border-t border-[#F3F0E7] hover:bg-[#FAF8F2]"
                      data-testid={`cdn-row-${it.name}`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          {it.orphan && (
                            <span title="Órfão — sem referência em BD"
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#9B1C1C] bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                              <AlertTriangle size={10} /> Órfão
                            </span>
                          )}
                          <span className="font-mono text-[12px] truncate max-w-[280px]" title={it.name}>{it.name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-[12px] text-[#0F1829]">
                        {it.ficha_nome ? (
                          <Link to={`/admin/cursos/inscricoes?curso=${cursoId}&ficha=${it.ficha_id}`}
                            className="hover:underline" title={`Ficha ${it.ficha_id}`}>
                            {it.ficha_nome}
                          </Link>
                        ) : <span className="text-[#978D78]">—</span>}
                      </td>
                      <td className="py-2 px-3">
                        {tipoCfg ? (
                          <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tipoCfg.cls}`}>
                            {tipoCfg.label}
                          </span>
                        ) : <span className="text-[#978D78] text-[11px]">—</span>}
                      </td>
                      <td className="py-2 px-3 text-right text-[12px] tabular-nums text-[#0F1829]">{_fmtBytes(it.size)}</td>
                      <td className="py-2 px-3 text-[12px] text-[#6B7280] whitespace-nowrap">{fmtDateTime(it.lastChanged)}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {it.url && (
                            <a href={it.url} target="_blank" rel="noreferrer"
                              data-testid={`cdn-download-${it.name}`}
                              title="Abrir ficheiro (pull-zone)"
                              className="p-1.5 border border-[#E6E1D6] rounded hover:bg-white text-[#0F1829]">
                              <ExternalLink size={12} />
                            </a>
                          )}
                          {it.orphan && (
                            <button type="button"
                              data-testid={`cdn-delete-${it.name}`}
                              onClick={() => handleDeleteOrphan(it.name)}
                              disabled={deletingName === it.name}
                              title="Apagar ficheiro órfão"
                              className="p-1.5 border border-red-200 rounded hover:bg-red-50 text-red-600 disabled:opacity-50">
                              {deletingName === it.name ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && filtered.length > 0 && (
        <p className="text-[11px] text-[#978D78]">
          {filtered.length} de {data.count} ficheiros · {_fmtBytes(filtered.reduce((s, it) => s + (it.size || 0), 0))}
        </p>
      )}
    </section>
  );
}



// ── Main page ──────────────────────────────────────────────────────
export default function AdminCursoInscricoes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursoId = searchParams.get("curso") || "";

  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(true);
  const [inscricoes, setInscricoes] = useState([]);
  const [inscLoading, setInscLoading] = useState(false);
  const [compras, setCompras] = useState([]);
  const [comprasLoading, setComprasLoading] = useState(false);
  const tab = searchParams.get("tab") || "inscricoes";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t && t !== "inscricoes") next.set("tab", t);
    else next.delete("tab");
    setSearchParams(next);
  };
  const [lookups, setLookups] = useState({});
  const [editing, setEditing] = useState(null); // null=closed, {} = new, {...} = edit
  const [auditTarget, setAuditTarget] = useState(null); // ficha p/ ver timeline
  const [filter, setFilter] = useState("");
  const [docFilter, setDocFilter] = useState("all"); // all | sem_ficha | sem_contrato | menor_sem_decl
  const [includePast, setIncludePast] = useState(false);
  const [incompletasComUploads, setIncompletasComUploads] = useState([]);
  const [showIncompletas, setShowIncompletas] = useState(false);

  const loadCursos = useCallback(async (withPast) => {
    setCursosLoading(true);
    try {
      const r = withPast
        ? await adminApi.cursosListIncludingPast()
        : await adminApi.cursosListActive();
      setCursos(r.data || []);
    } catch (e) { toast.error(e.message || "Erro a carregar cursos"); }
    finally { setCursosLoading(false); }
  }, []);

  useEffect(() => {
    loadCursos(includePast);
    adminApi.cursosLookups().then(setLookups).catch(() => {});
     
  }, []);

  useEffect(() => { loadCursos(includePast); }, [includePast, loadCursos]);

  const loadInscricoes = async (cid) => {
    if (!cid) return;
    setInscLoading(true);
    try {
      const r = await adminApi.cursoInscricoesList(cid);
      setInscricoes(r.data || []);
    } catch (e) { toast.error(e.message || "Erro a carregar inscrições"); }
    finally { setInscLoading(false); }
  };

  const loadCompras = async (cid) => {
    if (!cid) return;
    setComprasLoading(true);
    try {
      const r = await adminApi.cursoCompras(cid);
      setCompras(r.data || []);
    } catch (e) { toast.error(e.message || "Erro a carregar compras"); }
    finally { setComprasLoading(false); }
  };

  useEffect(() => {
    if (cursoId) {
      loadInscricoes(cursoId);
      loadCompras(cursoId);
      adminApi.cursoFichasIncompletasComUploads(cursoId)
        .then((r) => setIncompletasComUploads(r.data || []))
        .catch(() => setIncompletasComUploads([]));
    } else {
      setInscricoes([]);
      setCompras([]);
      setIncompletasComUploads([]);
    }
     
  }, [cursoId]);

  // ── Auto-refresh silencioso da lista de inscritos a cada 30s ──────
  // Evita o caso em que o admin abre o curso logo após o pagamento (PR)
  // e ainda não vê a auto-inscrição/auto-ficha que está a ser criada
  // pelo backend nos segundos seguintes. Refresca também quando o
  // separador volta a estar visível (foco da tab).
  useEffect(() => {
    if (!cursoId) return;
    const tick = async () => {
      try {
        const r = await adminApi.cursoInscricoesList(cursoId);
        setInscricoes(r.data || []);
      } catch { /* silent */ }
    };
    const id = setInterval(tick, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [cursoId]);

  const selectedCurso = useMemo(
    () => cursos.find((x) => String(x.ID_Curso) === String(cursoId)) || null,
    [cursos, cursoId],
  );
  const cursoDataInicio = selectedCurso?.DataInicio || null;

  const cursoLabel = useMemo(() => {
    const c = selectedCurso;
    if (!c) return cursoId || "";
    const desc = c.Descricao || `Produto ${c.ID_Produto || "?"}`;
    return `${desc} • #${c.ID_Curso} • ${c.Local || "—"} • ${fmtDate(c.DataInicio)}`;
  }, [selectedCurso, cursoId]);

  // Total ESPERADO de pessoas inscritas no curso (denominador unificado
  // usado pela "Saúde do Curso" e "Presenças em tempo real").
  //
  // A fonte primária é a Quantidade comprada por **link de inscrição**
  // (cada link representa uma compra, e cada compra pode trazer 1+
  // formandos). Adicionalmente, fichas avulsas sem link (criadas
  // manualmente pelo admin) contam +1 cada.
  //
  // Fallback: garantimos sempre >= nº de fichas existentes.
  const expectedTotal = useMemo(() => {
    const linkQty = new Map(); // ID_LinkInscricao → Quantidade
    let fichasSemLink = 0;
    for (const i of inscricoes) {
      const lid = i.ID_LinkInscricao;
      if (lid && i.link_progress) {
        linkQty.set(lid, Number(i.link_progress.quantidade) || 0);
      } else if (!lid) {
        fichasSemLink += 1;
      }
    }
    const totalLinks = Array.from(linkQty.values()).reduce((a, b) => a + b, 0);
    return Math.max(totalLinks + fichasSemLink, inscricoes.length);
  }, [inscricoes]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = inscricoes;
    if (q) {
      list = list.filter((i) =>
        (i.NomeCompleto || "").toLowerCase().includes(q) ||
        (i.Email || "").toLowerCase().includes(q) ||
        String(i.ID_Encomenda || "").includes(q) ||
        (i.Nif || "").toString().includes(q)
      );
    }
    if (docFilter !== "all") {
      list = list.filter((i) => {
        const minor = isMinor(i.DataNascimento, cursoDataInicio);
        const fichaEntregue = i.ficha_entregue ?? (!!i.FichaAssinadaPath || !!i.FichaConfirmadaEmailEm);
        const contratoEntregue = i.contrato_entregue ?? (!!i.ContratoAssinadoPath || !!i.ContratoConfirmadoEmailEm);
        const menorEntregue = i.menor_entregue ?? (!!i.MenorAssinadoPath || !!i.MenorConfirmadoEmailEm);
        if (docFilter === "sem_ficha") return !fichaEntregue;
        if (docFilter === "sem_contrato") return !contratoEntregue;
        if (docFilter === "menor_sem_decl") return minor && !menorEntregue;
        return true;
      });
    }
    return list;
  }, [inscricoes, filter, docFilter, cursoDataInicio]);

  const counts = useMemo(() => {
    let semFicha = 0, semContrato = 0, menorSemDecl = 0;
    inscricoes.forEach((i) => {
      // Repetentes não preenchem docs — não contam como pendentes.
      if (i.repetente) return;
      const fichaEntregue = i.ficha_entregue ?? (!!i.FichaAssinadaPath || !!i.FichaConfirmadaEmailEm);
      const contratoEntregue = i.contrato_entregue ?? (!!i.ContratoAssinadoPath || !!i.ContratoConfirmadoEmailEm);
      const menorEntregue = i.menor_entregue ?? (!!i.MenorAssinadoPath || !!i.MenorConfirmadoEmailEm);
      if (!fichaEntregue) semFicha++;
      if (!contratoEntregue) semContrato++;
      if (isMinor(i.DataNascimento, cursoDataInicio) && !menorEntregue) menorSemDecl++;
    });
    return { total: inscricoes.length, semFicha, semContrato, menorSemDecl };
  }, [inscricoes, cursoDataInicio]);

  const onDelete = async (ficha) => {
    if (!window.confirm(`Anular inscrição de ${ficha.NomeCompleto}?`)) return;
    try {
      await adminApi.cursoInscricaoDelete(cursoId, ficha.ID_FichaInscricao);
      toast.success("Inscrição anulada");
      loadInscricoes(cursoId);
      loadCompras(cursoId);
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-curso-inscricoes">
      <Link to="/admin" className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={12} /> Dashboard
      </Link>

      <header className="mb-6 flex items-start gap-3">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><GraduationCap size={22} /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
            Inscrições em Cursos
          </h1>
          <p className="text-sm text-[#4B5563]">Gere fichas de inscrição, gera ficheiros DOCX preenchidos e carrega versões assinadas (impressas + scaneadas).</p>
        </div>
      </header>

      <CursoTabsNav cursoId={cursoId}
        current={["compras", "documentos", "diplomas", "avaliacao"].includes(tab) ? tab : "inscricoes"}
        cursoLabel={selectedCurso ? `${selectedCurso.NomeCurso || selectedCurso.Descricao || `Produto ${selectedCurso.ID_Produto}`} • #${selectedCurso.ID_Curso}` : undefined} />

      {cursoId && <EntidadeFormadoraAviso cursoId={cursoId} />}

      <TemplatesCard />

      <section className="mb-4 p-4 bg-white border border-[#E6E1D6] rounded-md">
        <div className="flex items-center justify-between gap-3 mb-1">
          <label className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280]">Seleccionar curso</label>
          <label className="text-[11px] text-[#0F1829] inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="toggle-past-courses">
            <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} className="w-3.5 h-3.5 accent-[#0F1829]" />
            Mostrar cursos passados
          </label>
        </div>
        <select className="mt-1 w-full px-2.5 py-2 text-sm border border-[#E6E1D6] rounded bg-white focus:outline-none focus:border-[#0F1829]"
          value={cursoId} onChange={(e) => setSearchParams(e.target.value ? { curso: e.target.value } : {})}
          disabled={cursosLoading}
          data-testid="curso-picker">
          <option value="">{cursosLoading ? "A carregar…" : "— escolher curso —"}</option>
          {cursos.map((c) => {
            const past = c.DataFim && new Date(c.DataFim) < new Date();
            return (
              <option key={c.ID_Curso} value={c.ID_Curso}>
                {past ? "⏪ " : ""}{c.Descricao || `Produto ${c.ID_Produto || "?"}`} — #{c.ID_Curso}{c.ID_Produto != null ? ` (Produto #${c.ID_Produto})` : ""} — {c.Local || "—"} — Início {fmtDate(c.DataInicio)}
              </option>
            );
          })}
        </select>
      </section>

      {cursoId && (
        <>
          {tab === "inscricoes" && (
        <>
        {incompletasComUploads.length > 0 && (
          <section className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md"
            data-testid="incompletas-com-uploads-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {incompletasComUploads.length} {incompletasComUploads.length === 1 ? "formando devolveu" : "formandos devolveram"} documentos assinados sem preencher o formulário online
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Estes formandos preencheram à mão (Word/PDF) sem passar pelo formulário público — campos como Morada, Nº Doc ou Data de Nascimento ficaram com os valores sentinela do bulk-import. Contacte-os para regularizarem a inscrição.
                </p>
                <button type="button" onClick={() => setShowIncompletas((s) => !s)}
                  data-testid="incompletas-toggle"
                  className="mt-2 text-xs text-amber-900 underline hover:no-underline">
                  {showIncompletas ? "Esconder lista" : "Ver lista detalhada"}
                </button>
                {showIncompletas && (
                  <div className="mt-3 overflow-x-auto border border-amber-200 rounded bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-100/60 text-amber-900">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold">Nome</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Contacto</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Docs carregados</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Campos em falta</th>
                          <th className="text-right px-2 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {incompletasComUploads.map((r) => {
                          const insc = inscricoes.find((i) => i.ID_FichaInscricao === r.ID_FichaInscricao);
                          return (
                            <tr key={r.ID_FichaInscricao} className="border-t border-amber-100"
                              data-testid={`incompleta-row-${r.ID_FichaInscricao}`}>
                              <td className="px-2 py-1.5 text-[#0F1829] font-medium">{r.NomeCompleto || "—"}</td>
                              <td className="px-2 py-1.5 text-[#4B5563]">
                                {r.Email && <div><a className="underline" href={`mailto:${r.Email}`}>{r.Email}</a></div>}
                                {r.Telefone && <div className="text-[10px] text-[#6B7280]">{r.Telefone}</div>}
                              </td>
                              <td className="px-2 py-1.5">
                                {r.documentos_carregados.map((d) => (
                                  <span key={d} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">📎 {d}</span>
                                ))}
                              </td>
                              <td className="px-2 py-1.5 text-amber-900 text-[11px]">
                                {r.campos_em_falta.join(", ")}
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                {insc ? (
                                  <button type="button" onClick={() => setEditing(insc)}
                                    data-testid={`incompleta-edit-${r.ID_FichaInscricao}`}
                                    className="text-[11px] px-2 py-1 border border-[#0F1829] text-[#0F1829] rounded hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1">
                                    <Pencil size={10} /> Editar ficha
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-[#6B7280]">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
        <CourseHealthCard
          curso={selectedCurso}
          counts={counts}
          total={inscricoes.length}
          expectedTotal={expectedTotal}
          cursoId={cursoId}
        />
        <CheckinKpiStrip cursoId={cursoId} expectedTotal={expectedTotal} />
        <section className="p-4 bg-white border border-[#E6E1D6] rounded-md">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Inscrições — {cursoLabel}</h2>
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar nome/email/encomenda…"
              className="ml-auto px-2 py-1 text-xs border border-[#E6E1D6] rounded w-56"
              data-testid="insc-filter" />
            <button onClick={() => setEditing({})}
              className="text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540] inline-flex items-center gap-1.5"
              data-testid="insc-new">
              <UserPlus size={12} /> Nova
            </button>
            <ImportExcelButton cursoId={cursoId} onImported={() => loadInscricoes(cursoId)} />
            <CheckinOperadoresButton cursoId={cursoId} />
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="insc-doc-filters">
            {[
              { k: "all",            label: "Todas",                  count: counts.total },
              { k: "sem_ficha",      label: "Sem Ficha",              count: counts.semFicha,      tone: "amber" },
              { k: "sem_contrato",   label: "Sem Contrato",           count: counts.semContrato,   tone: "amber" },
              { k: "menor_sem_decl", label: "Menor sem Declaração",   count: counts.menorSemDecl,  tone: "purple" },
            ].map(({ k, label, count, tone }) => {
              const active = docFilter === k;
              const base = "px-2.5 py-1 text-[11px] rounded border inline-flex items-center gap-1.5 transition";
              const cls = active
                ? "bg-[#0F1829] text-white border-[#0F1829]"
                : tone === "amber"
                  ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                  : tone === "purple"
                    ? "bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100"
                    : "bg-white text-[#0F1829] border-[#E6E1D6] hover:bg-[#FAF8F2]";
              return (
                <button key={k} type="button" onClick={() => setDocFilter(k)}
                  data-testid={`chip-${k}`} className={`${base} ${cls}`}>
                  {label}
                  <span className={`text-[10px] font-bold ${active ? "opacity-90" : "opacity-70"}`}>{count}</span>
                </button>
              );
            })}
          </div>
          {/* Sprint 1G — Legenda dos estados de documentos */}
          <div className="flex flex-wrap items-center gap-3 mb-3 text-[10px] text-[#6B7280]" data-testid="doc-states-legend">
            <span className="font-semibold uppercase tracking-wider">Estados:</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-emerald-200 border border-emerald-300" />
              <CheckCircle2 size={10} className="text-emerald-700" /> Upload do cliente
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-amber-200 border border-amber-300" />
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Pendente
            </span>
          </div>
          {inscLoading ? (
            <p className="text-xs text-[#6B7280] flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> A carregar...
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-[#978D78] italic">
              {docFilter !== "all" || filter
                ? "Nenhuma inscrição corresponde aos filtros."
                : "Sem inscrições."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#6B7280] border-b border-[#E6E1D6]">
                    <th className="py-2 pr-2">#</th>
                    <th className="py-2 pr-2">Nome</th>
                    <th className="py-2 pr-2">Email</th>
                    <th className="py-2 pr-2">Telefone</th>
                    <th className="py-2 pr-2">Registo</th>
                    <th className="py-2 pr-2" title="Estado do email com o link público enviado ao formando">Email Link</th>
                    <th className="py-2 pr-2">Estado Docs</th>
                    <th className="py-2 pr-2">Acções</th>
                    <th className="py-2 pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i) => {
                    const minor = isMinor(i.DataNascimento, cursoDataInicio);
                    return (
                    <tr key={i.ID_FichaInscricao} className="border-b border-[#F3F0E7]" data-testid={`insc-row-${i.ID_FichaInscricao}`}>
                      <td className="py-2 pr-2 text-[11px] text-[#978D78] font-mono">{i.ID_FichaInscricao}</td>
                      <td className="py-2 pr-2 font-medium">
                        <div>{i.NomeCompleto}</div>
                        <div className="mt-0.5"><CompraChip compra={i.CompraInfo} /></div>
                        {i.link_progress && <LinkProgressChip lp={i.link_progress} />}
                      </td>
                      <td className="py-2 pr-2 truncate max-w-[200px]">{i.Email}</td>
                      <td className="py-2 pr-2">{i.Telefone || "—"}</td>
                      <td className="py-2 pr-2 text-[12px]">{fmtDate(i.DataRegisto)}</td>
                      <td className="py-2 pr-2"><EmailLinkPill ficha={i} cursoId={cursoId} onResent={() => loadInscricoes(cursoId)} /></td>
                      <td className="py-2 pr-2">
                        <DocStatusPills ficha={i} minor={minor} />
                        <CheckinPills checkins={i.checkins} />
                      </td>
                      <td className="py-2 pr-2">
                        <DocActionsCell cursoId={cursoId} ficha={i} minor={minor}
                          onChanged={() => loadInscricoes(cursoId)} />
                      </td>
                      <td className="py-2 pr-2 flex gap-1 justify-end">
                        <ValidarButton ficha={i} cursoId={cursoId} onValidated={() => loadInscricoes(cursoId)} />
                        <PrintQrButton ficha={i} cursoNome={selectedCurso?.NomeCurso || selectedCurso?.Descricao || ""} />
                        <button onClick={() => setAuditTarget(i)} title="Histórico de actividade"
                          data-testid={`insc-audit-${i.ID_FichaInscricao}`}
                          className="p-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]">
                          <History size={12} />
                        </button>
                        <button
                          onClick={async () => {
                            const newVal = !i.repetente;
                            const ok = newVal
                               
                              ? window.confirm(`Marcar ${i.NomeCompleto} como "A repetir o curso"?\n\nO formando deixa de precisar preencher documentos e não vai aparecer nas fichas de presença.`)
                              : true;
                            if (!ok) return;
                            try {
                              await adminApi.cursoInscricaoSetRepetente(cursoId, i.ID_FichaInscricao, newVal);
                              toast.success(newVal ? "Marcado como A Repetir." : "Marcação A Repetir removida.");
                              loadInscricoes(cursoId);
                            } catch (e) {
                              toast.error(e.message || "Erro");
                            }
                          }}
                          title={i.repetente ? "Desmarcar 'A repetir o curso'" : "Marcar como 'A repetir o curso' (não preenche docs nem entra em fichas de presença)"}
                          data-testid={`insc-repetente-${i.ID_FichaInscricao}`}
                          className={`p-1 border rounded inline-flex items-center justify-center ${i.repetente ? "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100" : "border-[#E6E1D6] hover:bg-[#FAF8F2]"}`}
                        >
                          <RefreshCcw size={12} />
                        </button>
                        <button onClick={() => setEditing(i)} title="Editar"
                          data-testid={`insc-edit-${i.ID_FichaInscricao}`}
                          className="p-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]">
                          <Pencil size={12} />
                        </button>
                        {i.LinkToken && (
                          <button
                            type="button"
                            onClick={async () => {
                              const url = `${window.location.origin}/cursos/inscricao/${i.LinkToken}`;
                              try {
                                await navigator.clipboard.writeText(url);
                                toast.success("Link do formulário copiado");
                              } catch {
                                window.prompt("Copie o link:", url);
                              }
                            }}
                            title={`Copiar link do formulário (envia ao formando se necessário): ${window.location.origin}/cursos/inscricao/${i.LinkToken}`}
                            data-testid={`insc-copy-link-${i.ID_FichaInscricao}`}
                            className="p-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] text-[#0F1829]">
                            <Link2 size={12} />
                          </button>
                        )}
                        <button onClick={() => onDelete(i)} title="Anular"
                          data-testid={`insc-del-${i.ID_FichaInscricao}`}
                          className="p-1 border border-[#E6E1D6] rounded hover:bg-red-50 text-red-600">
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        </>
          )}

          {tab === "compras" && (
            <ComprasTab
              compras={compras}
              loading={comprasLoading}
              cursoLabel={cursoLabel}
              cursoId={cursoId}
              inscricoes={inscricoes}
              onReload={() => loadCompras(cursoId)}
              onInscricoesChanged={() => loadInscricoes(cursoId)}
              onEditFicha={(ficha) => setEditing(ficha)}
              onDeleteFicha={onDelete}
            />
          )}
          {tab === "documentos" && (
            <DocumentosTab cursoId={cursoId} />
          )}
          {tab === "avaliacao" && (
            <AvaliacaoTab cursoId={cursoId} cursoLabel={cursoLabel} />
          )}
          {tab === "diplomas" && (
            <DiplomasTab cursoId={cursoId} inscricoes={inscricoes} onReload={() => loadInscricoes(cursoId)} />
          )}
          {tab === "cdn" && (
            <CdnFilesTab cursoId={cursoId} />
          )}
        </>
      )}

      {editing !== null && cursoId && (
        <InscricaoForm
          cursoId={cursoId}
          ficha={editing}
          lookups={lookups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadInscricoes(cursoId); }}
        />
      )}

      <AuditTimelineModal
        open={auditTarget !== null}
        onClose={() => setAuditTarget(null)}
        cursoId={cursoId}
        ficha={auditTarget}
      />
    </div>
  );
}
